import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';
import { AppCartService } from '../../components/catalogue/components/app-cart/app-cart.service';
import { CatalogueService } from '../../components/catalogue/catalogue.service';
import { CheckOutService } from '../../components/checkout/checkout.service';
import { ProductTemplateService } from '../../components/product-template/services/product-template.service';
import { SearchAllService } from '../../components/search-all/search-all.service';
import { BusinessType, TaskType } from '../../enums/enum';
import { MessageService } from '../../services/message.service';
import { SessionService } from '../../services/session.service';
import { OrderWithAiService } from './order-with-ai.service';

interface OrderAiMessage {
  role: 'ai' | 'assistant' | 'user' | 'system';
  text?: string;
  type?: string;
  data?: any;
  messageId?: string;
  transient?: boolean;
}

@Component({
  selector: 'app-order-with-ai',
  templateUrl: './order-with-ai.component.html',
  styleUrls: ['./order-with-ai.component.scss']
})
export class OrderWithAiComponent implements OnInit, OnDestroy {
  isOpen = false;
  isHidden = false;
  isTyping = false;
  typingLabel = 'Thinking';
  prompt = '';
  sessionId = '';
  intent: any;
  selectedMerchant: any;
  selectedProduct: any;
  selectedProductMerchant: any;
  lastMerchantContext: any;
  selectedQuantity = 1;
  scheduleSlideValue = 0;
  scheduleSlideCompleteThreshold = 40;
  scheduleSlideCompleting = false;
  merchantCandidates: any[] = [];
  productCandidates: any[] = [];
  customizationGroups: any[] = [];
  selectedCustomizations = {};
  addonErrors: string[] = [];
  templateFields: any[] = [];
  selectedServiceSchedule: any;
  pendingServiceSchedule: any;
  serviceAgents: any[] = [];
  serviceStartSlots: any[] = [];
  serviceEndSlots: any[] = [];
  pendingProductCandidate: any;
  pendingCurrentStoreSearch: { merchant: any; searchText: string };
  messages: OrderAiMessage[] = [];
  chatrooms: any[] = [];
  activeChatroom: any;
  activeChatId = '';
  renamingChatId = '';
  renameTitleDraft = '';
  deleteConfirmChatId = '';
  chatActionPendingId = '';
  transientGreeting = '';
  currentStoreSuggestion: any;
  currentStoreSuggestionDismissed = false;
  isChatroomsLoading = false;
  isMessagesLoading = false;
  chatPersistenceReady = false;
  isDragging = false;
  isMobilePanelOpen = false;
  // Mobile-only (desktop always shows both panes side by side, see the max-width:768px
  // media query gating this). Defaults to 'list' for a brand new session; opening a
  // chat (tap a row, hit New, or send a first message) flips this to 'chat', and the
  // back button in the mobile header flips it back -- same list<->thread model as
  // WhatsApp, chat-list stays reachable rather than only ever showing one pane.
  mobileChatView: 'list' | 'chat' = 'list';
  showIntro = false;
  introStepIndex = 0;
  displayedTitle = '';
  displayedText = '';
  // Populated from language strings at intro-start time (see buildIntroSteps) so a
  // marketplace can translate these via order_with_ai_title_1..3 /
  // order_with_ai_description_1..3; the strings below are only the English fallback.
  introSteps: { title: string; text: string }[] = [];
  // Small looping icon strip shown throughout the intro. Plain Unicode emoji -- these
  // are just text glyphs rendered by the browser's own OS/system font, same as any
  // other character on the page, so there is no SVG markup, viewBox, currentColor,
  // display binding, or animation-timing to get wrong. Mixed product + service icons on
  // purpose -- this ships across every vertical on the platform, not just food.
  demoItems: string[] = ['🍕', '👕', '📱', '✂️', '🔧', '📦'];
  demoItemIndex = 0;

  // Fills the mobile chat list's leftover space (barren with 0-3 saved chats, sidebar
  // is the whole screen there) with tappable starter prompts. Deliberately no hardcoded
  // fallback list here -- these are meant to reflect what a specific marketplace
  // actually sells (pizza/shirt placeholders make no sense for e.g. a hardware store),
  // so an unconfigured marketplace shows nothing rather than a generic default set. See
  // *ngIf on .owai-quick-prompts in the template, gated on this being non-empty.
  // Capped to 4 -- matches the dashboard extension's own max. No per-prompt icon field:
  // this table's column can't store emoji (non-BMP characters silently come back as a
  // literal "?" -- a MySQL charset limitation, not fixable from app code), so every tile
  // shows the same static CSS atom mark in the template instead of user-entered emoji.
  getQuickPromptSuggestions(): { label: string; prompt: string }[] {
    // Array.isArray, not just `... || []`: that only catches null/undefined -- if
    // prompts ever came back truthy-but-not-an-array (an object, a string), `|| []`
    // would not fire and .slice() below would throw. This data has already come back
    // malformed more than once this session, so the shape isn't guaranteed. p?.label/
    // p?.prompt: a null/malformed entry in the array shouldn't be able to throw either
    // -- this is read directly from the template, so an uncaught throw here breaks the
    // whole sidebar's rendering, not just this section.
    const rawPrompts = this.getOrderWithAiConfig().prompts;
    const configPrompts = Array.isArray(rawPrompts) ? rawPrompts : [];
    return configPrompts.slice(0, 4).map(p => ({ label: p?.label || p?.prompt || '', prompt: p?.prompt || p?.label || '' }));
  }

  // Required because the *ngFor above iterates getQuickPromptSuggestions() directly --
  // that returns a brand-new array of brand-new objects on every change-detection cycle
  // (which fires constantly: timers, animations, mouse events), so without a stable
  // trackBy Angular's default identity check treats the whole list as different every
  // single cycle and destroys+recreates the DOM nodes for it continuously. If a CD cycle
  // landed between a tap's mousedown and its click, the element under the finger had
  // already been swapped out by the time click would have fired -- the tap silently did
  // nothing, no error, because the click event never had a live element to land on.
  trackByPrompt(index: number, suggestion: { label: string; prompt: string }): string {
    return suggestion.prompt;
  }
  introFinale = false;
  private demoInterval: any = null;
  widgetLeft = 0;
  widgetTop = 0;
  private suppressNextClick = false;
  private widgetPosition = { x: 0, y: 0 };
  private dragStart = { pointerX: 0, pointerY: 0, widgetX: 0, widgetY: 0 };
  private readonly viewportMargin = 16;
  private readonly bottomSafeArea = 104;
  private readonly launcherSize = { width: 176, height: 58 };
  private readonly panelSize = { width: 420, height: 680 };
  private refreshedMerchantIds: { [merchantId: string]: boolean } = {};
  private refreshedMerchantContexts: { [merchantId: string]: any } = {};
  private readonly actionMessageTypes = [
    'merchant-candidates',
    'merchant-actions',
    'current-store-confirm',
    'product-candidates',
    'product-options',
    'quantity',
    'cart-conflict',
    'cart-summary',
    'followup',
    'service-agent-candidates',
    'service-date-options',
    'service-start-slots',
    'service-end-slots',
    'service-surge-confirm',
    'service-schedule-summary'
  ];
  private routerSub: Subscription;
  private introTimers: any[] = [];
  private static readonly INTRO_SEEN_KEY = 'owai_intro_seen';
  // How long each step stays on screen (fade-in wave + reading time) before advancing.
  private static readonly INTRO_STEP_HOLD_MS = 3000;
  private static readonly INTRO_FINALE_MS = 1300;
  private static readonly INTRO_DEMO_CYCLE_MS = 2200;
  // Fallback accent when a marketplace has no theme color configured yet.
  private static readonly DEFAULT_ACCENT_HEX = '#9be15d';
  private static readonly DEFAULT_ACCENT_RGB = '155, 225, 93';
  // Same placeholder asset the rest of the app falls back to (search-all.html etc)
  // when a merchant/product has no real image.
  private static readonly IMAGE_PLACEHOLDER = 'assets/img/image-placeholder.svg';
  // Safety net for every API call this widget makes: without this, a request that
  // never resolves (dropped connection, hung backend) left the chat stuck on a
  // "Checking store"/"Searching..." indicator forever with no way to recover short of
  // reloading the page -- there was no timeout anywhere in this file before.
  private static readonly API_TIMEOUT_MS = 15000;
  private readonly boundDragMove = (event: MouseEvent | TouchEvent) => this.onDragMove(event);
  private readonly boundDragEnd = () => this.endDrag();
  private readonly boundOutsidePointer = (event: MouseEvent | TouchEvent) => this.closeOnOutsidePointer(event);

  constructor(
    private elementRef: ElementRef,
    private router: Router,
    private sessionService: SessionService,
    private orderWithAiService: OrderWithAiService,
    private searchAllService: SearchAllService,
    private catalogueService: CatalogueService,
    private checkoutService: CheckOutService,
    private cartService: AppCartService,
    private productTemplateService: ProductTemplateService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.sessionId = this.getSessionId();
    this.isHidden = this.shouldHideForRoute(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isHidden = this.shouldHideForRoute(event.urlAfterRedirects || event.url);
        if (this.isHidden) {
          this.isOpen = false;
          this.detachOutsideCloseListeners();
        }
        this.clampWidgetPosition();
      });
    this.messages = [this.getStarterMessage()];
    this.initializeWidgetPosition();
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
    this.detachOutsideCloseListeners();
    this.clearIntroTimers();
    this.endDrag();
  }

  toggleOpen() {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.attachOutsideCloseListeners();
      this.loadChatrooms();
      if (!this.sessionService.getString(OrderWithAiComponent.INTRO_SEEN_KEY)) {
        this.startIntroSequence();
      }
    } else {
      this.detachOutsideCloseListeners();
      this.clearIntroTimers();
      this.showIntro = false;
    }
    this.clampWidgetPosition();
  }

  startIntroSequence() {
    this.showIntro = true;
    this.clearIntroTimers();
    this.introFinale = false;
    this.introSteps = this.buildIntroSteps();
    this.demoItemIndex = 0;
    this.demoInterval = setInterval(() => {
      this.demoItemIndex = (this.demoItemIndex + 1) % this.demoItems.length;
    }, OrderWithAiComponent.INTRO_DEMO_CYCLE_MS);
    this.goToIntroStep(0);
  }

  skipIntro() {
    this.finishIntro();
  }

  // Reads the dashboard-configured Order with AI override (set via the marketplace
  // extension: "order_with_ai_config" JSON, exposed on config by
  // marketPlaceFetchAppConfiguration). {} when the merchant hasn't configured anything.
  private getOrderWithAiConfig(): { steps?: { title?: string; text?: string }[]; prompts?: { label?: string; prompt?: string }[] } {
    const config = this.sessionService.get('config') || {};
    return config.order_with_ai_config || {};
  }

  // Priority for each step's title/text: dashboard config (per-marketplace override,
  // set via the Order with AI extension) -> language strings (order_with_ai_title_1..3 /
  // order_with_ai_description_1..3, for marketplaces translating via the language-string
  // system instead) -> English fallback below. Any of the three can be set independently
  // per field, so a merchant who only customized step 1 still gets sensible defaults for
  // steps 2-3.
  private buildIntroSteps(): { title: string; text: string }[] {
    const strings = this.sessionService.languageStrings || {};
    const configSteps = this.getOrderWithAiConfig().steps || [];
    const defaults = [
      { title: 'Just say what you want', text: 'Order food in your own words — "2 pizzas from Garden Grill", or whatever you\'re craving.' },
      { title: 'We find it, made your way', text: 'Matched to real restaurants near you, customized just how you like it.' },
      { title: 'Checkout in one tap', text: 'Everything carries over — review and confirm, done.' }
    ];
    return defaults.map((fallback, i) => {
      const configStep = configSteps[i] || {};
      const stringTitle = strings['order_with_ai_title_' + (i + 1)];
      const stringText = strings['order_with_ai_description_' + (i + 1)];
      return {
        title: configStep.title || stringTitle || fallback.title,
        text: configStep.text || stringText || fallback.text
      };
    });
  }

  // No typewriter, no per-letter animation -- the step's title/text shows immediately
  // and fades in as one plain block (see .owai-intro__text-fade). Simpler and reads
  // cleaner than the letter-by-letter version.
  private goToIntroStep(step: number) {
    this.introStepIndex = step;
    this.displayedTitle = this.introSteps[step].title;
    this.displayedText = this.introSteps[step].text;
    this.introTimers.push(setTimeout(() => {
      if (step + 1 < this.introSteps.length) {
        this.goToIntroStep(step + 1);
      } else {
        this.playIntroFinale();
      }
    }, OrderWithAiComponent.INTRO_STEP_HOLD_MS));
  }

  // Once all steps have shown, the card/cart scene grows and takes over the center of
  // the screen for a beat before the intro closes into the actual chat.
  private playIntroFinale() {
    this.introFinale = true;
    this.introTimers.push(setTimeout(() => {
      this.finishIntro();
    }, OrderWithAiComponent.INTRO_FINALE_MS));
  }


  private finishIntro() {
    this.clearIntroTimers();
    this.showIntro = false;
    this.introFinale = false;
    this.sessionService.setString(OrderWithAiComponent.INTRO_SEEN_KEY, '1');
  }

  private clearIntroTimers() {
    this.introTimers.forEach(timer => clearTimeout(timer));
    this.introTimers = [];
    if (this.demoInterval) {
      clearInterval(this.demoInterval);
      this.demoInterval = null;
    }
  }

  clearChat() {
    this.prompt = '';
    this.clearTyping();
    this.resetSelectionState();
    this.lastMerchantContext = null;
    this.sessionId = this.createSessionId();
    this.sessionService.setString('order_with_ai_session_id', this.sessionId);
    if (!this.chatPersistenceReady || !this.activeChatId) {
      this.messages = [this.getStarterMessage()];
      return;
    }
    this.setTyping('Clearing chat');
    this.orderWithAiService.chatrooms(this.getChatApiPayload({
      action: 'clear',
      chatId: this.activeChatId
    })).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        const data = this.extractApiData(response);
        if (data && data.chatroom) {
          this.setActiveChatroom(data.chatroom);
        }
        this.messages = [this.getStarterMessage()];
        this.transientGreeting = '';
      },
      () => {
        this.clearTyping();
        this.addAiText('I could not clear this chat right now.');
      }
    );
  }

  startDrag(event: MouseEvent | TouchEvent) {
    // Only isOpen blocks this now -- dragging the collapsed launcher makes sense on
    // mobile too (it can sit over content there same as desktop); isMobileViewport()
    // used to block it outright, which is exactly what made the launcher undraggable
    // on mobile web. touchmove/touchend/touchcancel are already wired below, same as
    // the mouse events -- this only needed the early return removed.
    if (this.isOpen) {
      return;
    }
    const point = this.getPointerPoint(event);
    this.isDragging = true;
    this.suppressNextClick = false;
    this.dragStart = {
      pointerX: point.x,
      pointerY: point.y,
      widgetX: this.widgetPosition.x,
      widgetY: this.widgetPosition.y
    };
    document.addEventListener('mousemove', this.boundDragMove);
    document.addEventListener('mouseup', this.boundDragEnd);
    document.addEventListener('touchmove', this.boundDragMove, { passive: false });
    document.addEventListener('touchend', this.boundDragEnd);
    document.addEventListener('touchcancel', this.boundDragEnd);
  }

  sendPromptFromEnter(event: KeyboardEvent) {
    event.preventDefault();
    this.sendPrompt();
  }

  sendPrompt() {
    const text = (this.prompt || '').trim();
    if (!text || this.isTyping) {
      return;
    }
    this.blurPromptInput();
    if (!this.activeChatId) {
      this.createChatroom(null, () => {
        this.prompt = text;
        this.sendPrompt();
      });
      return;
    }
    this.pushLocalUserMessage(text);
    this.prompt = '';
    this.transientGreeting = '';
    this.currentStoreSuggestion = null;
    if (this.handlePendingCurrentStoreReply(text)) {
      this.persistMessages([{ role: 'user', text }], { lastUserPrompt: text });
      return;
    }
    const activeMerchant = this.selectedMerchant || this.selectedProductMerchant || this.lastMerchantContext;
    const reorderProductSearch = this.getReorderProductSearchText(text);
    if (reorderProductSearch && activeMerchant) {
      this.resetSelectionState();
      this.selectedMerchant = activeMerchant;
      this.lastMerchantContext = activeMerchant;
      this.persistMessages([{ role: 'user', text }], {
        lastUserPrompt: text,
        pendingProductSearch: reorderProductSearch
      });
      this.searchMerchantProducts(reorderProductSearch, activeMerchant);
      return;
    }
    this.resetSelectionState();
    if (activeMerchant) {
      this.selectedMerchant = activeMerchant;
      this.lastMerchantContext = activeMerchant;
    }
    this.setTyping('Understanding your order');
    let payload;
    try {
      payload = Object.assign(this.buildParsePayload(text), {
        chatId: this.activeChatId,
        prompt: text
      });
    } catch (error) {
      this.clearTyping();
      this.addAiText('I could not prepare the request. Please refresh and try again.');
      return;
    }
    let messageRequest;
    try {
      messageRequest = this.orderWithAiService.sendMessage(payload);
    } catch (error) {
      this.clearTyping();
      this.addAiText('I could not start the request. Please refresh and try again.');
      return;
    }
    messageRequest.pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (response && response.status && response.status !== 200) {
          this.addAiText(response.message || 'I could not understand that request right now.');
          return;
        }
        const data = this.extractApiData(response);
        if (data && data.chatroom) {
          this.setActiveChatroom(data.chatroom, false);
        }
        if (data && data.assistantMessage) {
          this.messages.push(this.normalizeStoredMessage(data.assistantMessage));
        }
        const intent = data && data.intent ? data.intent : response && response.intent;
        this.intent = intent || {};
        const directive = data && data.directive ? data.directive : null;
        if (directive) {
          this.handleChatDirective(directive, !!(data && data.assistantMessage));
          return;
        }
        this.handleLegacyIntent();
      },
      error => {
        this.clearTyping();
        if (error && error.status === 401) {
          this.addAiText(`Please login to use saved ${this.getBrandLabel()} chats.`);
          return;
        }
        this.addAiText('I could not read that request right now. Please try again.');
      }
    );
  }

  useFollowup(text: string) {
    if (text === 'Search product only') {
      const merchant = this.selectedMerchant || this.selectedProductMerchant || this.lastMerchantContext;
      this.removeChoiceMessages('followup');
      if (merchant) {
        this.selectedMerchant = merchant;
        this.lastMerchantContext = merchant;
        this.addFollowup('Which product should I search from ' + this.getMerchantName(merchant) + '?');
      } else {
        this.addFollowup('Which product should I search for?');
      }
      return;
    }
    this.prompt = text;
    this.sendPrompt();
  }

  searchPendingCurrentStoreProducts() {
    if (!this.pendingCurrentStoreSearch) {
      return;
    }
    const pending = this.pendingCurrentStoreSearch;
    this.removeChoiceMessages('current-store-confirm');
    this.pendingCurrentStoreSearch = null;
    this.selectedMerchant = pending.merchant;
    this.lastMerchantContext = pending.merchant;
    this.syncContext({
      selectedMerchant: pending.merchant,
      pendingAction: '',
      pendingProductSearch: pending.searchText
    });
    this.addAiText('Store selected: ' + this.getMerchantName(pending.merchant));
    this.searchMerchantProducts(pending.searchText, pending.merchant);
  }

  searchPendingProductEverywhere() {
    if (!this.pendingCurrentStoreSearch) {
      return;
    }
    const searchText = this.pendingCurrentStoreSearch.searchText;
    this.removeChoiceMessages('current-store-confirm');
    this.pendingCurrentStoreSearch = null;
    this.selectedMerchant = null;
    this.lastMerchantContext = null;
    this.syncContext({
      selectedMerchant: null,
      pendingAction: '',
      pendingProductSearch: searchText
    });
    this.searchProducts(searchText);
  }

  selectMerchant(merchant: any) {
    this.selectedMerchant = merchant;
    this.lastMerchantContext = merchant;
    this.addAssistantMessage({
      role: 'ai',
      type: 'selected',
      text: 'Store selected: ' + this.getMerchantName(merchant)
    }, {
      selectedMerchant: merchant,
      pendingAction: '',
      pendingProductSearch: '',
      pendingMerchantSelection: null
    });
    this.setMerchantSession(merchant);
    if (this.intent && this.intent.productSearchText) {
      this.searchProducts(this.intent.productSearchText);
    } else {
      this.addAssistantMessage({
        role: 'ai',
        type: 'merchant-actions',
        text: 'What should I show from ' + this.getMerchantName(merchant) + '?',
        data: merchant
      }, {
        selectedMerchant: merchant,
        pendingAction: 'merchant-actions'
      });
    }
  }

  showProductsForSelectedMerchant() {
    if (!this.selectedMerchant) {
      this.addFollowup('Please choose a store first.');
      return;
    }
    this.fetchMerchantProducts(this.selectedMerchant);
  }

  selectProduct(candidate: any, allowMerchantRefresh = true) {
    // Every exit path below (cart-conflict, an availability/limit message, or a
    // successful selection) used to leave isTyping stuck true forever once the
    // merchant-refresh branch a few lines down had fired: it calls setTyping('Checking
    // store') but nothing later in this function ever calls clearTyping() -- unlike
    // searchMerchantProducts/fetchMerchantProducts, which follow the same
    // refresh-then-continue shape but always route through another subscribe that does
    // clear it. Since isTyping also disables the composer and quick-prompt tiles, a
    // stuck true here made the whole widget look frozen even though the cart-conflict
    // prompt (or whatever message showed) was actually fine and clickable.
    this.clearTyping();
    const product = candidate && candidate.product;
    const rawMerchant = candidate && (candidate.merchant || this.selectedMerchant);
    if (!product) {
      this.addFollowup('Please choose a product first.');
      return;
    }
    if (allowMerchantRefresh && this.shouldRefreshMerchantContext(rawMerchant)) {
      this.setTyping('Checking store');
      this.refreshMerchantDetails(rawMerchant,
        refreshedMerchant => this.selectProduct(Object.assign({}, candidate, { merchant: refreshedMerchant }), false),
        () => this.selectProduct(candidate, false)
      );
      return;
    }
    const merchant = this.buildMerchantSessionContext(rawMerchant, product);
    if (!merchant) {
      this.addFollowup('Please choose a store first.');
      return;
    }
    const availabilityMessage = this.getProductAvailabilityMessage(product, merchant);
    if (availabilityMessage) {
      this.addAiText(availabilityMessage);
      return;
    }
    if (this.hasDifferentMerchantCart(merchant)) {
      const normalizedCandidate = Object.assign({}, candidate, { product, merchant });
      this.pendingProductCandidate = normalizedCandidate;
      this.addAssistantMessage({
        role: 'ai',
        type: 'cart-conflict',
        text: 'Your cart already has items from another store. Clear the cart to continue with ' + this.getMerchantName(merchant) + '?',
        data: { merchant, candidate: normalizedCandidate }
      }, {
        selectedMerchant: merchant,
        selectedProduct: product,
        pendingAction: 'cart-conflict'
      });
      return;
    }
    if (!this.canAddAnotherProductForMerchant(merchant)) {
      this.addAiText('This store allows only one product at a time. Please checkout or clear the cart before adding another product.');
      return;
    }
    this.selectedMerchant = merchant;
    this.selectedProductMerchant = merchant;
    this.lastMerchantContext = merchant;
    this.selectedProduct = product;
    this.setMerchantSession(merchant, product);
    this.selectedQuantity = this.getDefaultQuantity(this.selectedProduct);
    this.selectedServiceSchedule = null;
    this.pendingServiceSchedule = null;
    this.clearPreOrderTimeForProductContext(this.selectedProduct, merchant);
    this.addAssistantMessage({
      role: 'ai',
      type: 'selected',
      text: 'Product selected: ' + this.getProductName(this.selectedProduct)
    }, {
      selectedMerchant: merchant,
      selectedProduct: this.selectedProduct,
      pendingQuantity: this.selectedQuantity,
      pendingAction: 'product-selected'
    });
    if (this.shouldScheduleServiceProduct(this.selectedProduct, merchant)) {
      this.beginServiceScheduling();
      return;
    }
    this.prepareProductOptions();
  }

  clearCartAndContinue(candidateFromMessage?: any) {
    const candidate = this.pendingProductCandidate || candidateFromMessage;
    this.pendingProductCandidate = null;
    this.removeChoiceMessages('cart-conflict');
    if (!candidate) {
      this.addAiText('No pending product found. Please choose a product again.');
      return;
    }
    this.cartService.cartClearCall();
    this.cartService.updateStatus();
    // false: candidate.merchant is already the freshly-refreshed context from the
    // selectProduct() call that got us to this cart-conflict prompt in the first place
    // (see normalizedCandidate there) -- re-running the refresh here would just repeat
    // the same "Checking store" round trip for no reason.
    this.selectProduct(candidate, false);
  }

  cancelCartConflict() {
    if (!this.pendingProductCandidate) {
      return;
    }
    this.pendingProductCandidate = null;
    this.removeChoiceMessages('cart-conflict');
    this.addAiText('No changes made to your cart.');
  }

  cancelSelection() {
    this.pendingProductCandidate = null;
    this.addAiText('Okay, tell me what you want to order next.');
  }

  askMoreFromSameStore() {
    const merchant = this.selectedProductMerchant || this.selectedMerchant || this.lastMerchantContext;
    this.resetSelectionState(false);
    this.selectedMerchant = merchant;
    this.lastMerchantContext = merchant;
    this.syncContext({
      selectedMerchant: merchant,
      pendingAction: 'followup',
      pendingProductSearch: ''
    });
    this.addFollowup('Tell me the next product from ' + this.getMerchantName(merchant) + '.');
  }

  setQuantity(value: number) {
    const min = this.getMinimumQuantity(this.selectedProduct);
    const max = this.getAllowedMaxQuantity(this.selectedProduct);
    let next = Number(value) || min;
    next = Math.max(min, next);
    if (max > 0) {
      next = Math.min(max, next);
    }
    this.selectedQuantity = next;
  }

  // setQuantity() already clamps silently at these bounds -- a product with
  // maximum_quantity 1 (the real page's Add/Remove-toggle case, no stepper at all)
  // was previously just a no-op click with no feedback that anything happened.
  // Disabling the buttons at the bound makes that visible instead of silent.
  isQuantityAtMin(): boolean {
    return this.selectedQuantity <= this.getMinimumQuantity(this.selectedProduct);
  }

  isQuantityAtMax(): boolean {
    const max = this.getAllowedMaxQuantity(this.selectedProduct);
    return max > 0 && this.selectedQuantity >= max;
  }

  setScheduleQuantity(value: number, actionData?: any) {
    this.restoreProductActionState(actionData, {
      preserveLiveSelections: this.shouldPreserveLiveProductActionState(actionData)
    });
    this.setQuantity(value);
  }

  onScheduleSlideInput(event: Event) {
    if (this.scheduleSlideCompleting) {
      return;
    }
    const input = event ? event.target as HTMLInputElement : null;
    const value = input ? Number(input.value) || 0 : 0;
    this.scheduleSlideValue = Math.max(0, Math.min(100, value));
  }

  slideServiceScheduleToCart(event: Event, actionData?: any) {
    this.onScheduleSlideInput(event);
    if (this.scheduleSlideCompleting) {
      return;
    }
    if (this.scheduleSlideValue < this.scheduleSlideCompleteThreshold) {
      this.scheduleSlideValue = 0;
      return;
    }
    this.scheduleSlideCompleting = true;
    this.scheduleSlideValue = 100;
    setTimeout(() => {
      this.addCurrentProductToCart(actionData);
      this.scheduleSlideCompleting = false;
      this.scheduleSlideValue = 0;
    }, 220);
  }

  toggleCustomization(group: any, option: any) {
    this.addonErrors = [];
    const key = this.getCustomizationKey(group);
    if (this.isMultiSelectCustomization(group)) {
      const selected = this.selectedCustomizations[key] || [];
      const index = selected.indexOf(option);
      if (index > -1) {
        selected.splice(index, 1);
      } else {
        const max = this.getCustomizationMaximum(group);
        if (max > 0 && selected.length >= max) {
          this.addonErrors = ['Select up to ' + max + ' option(s) for ' + this.getCustomizationName(group) + '.'];
          return;
        }
        selected.push(option);
        this.applyDefaultCustomizations(this.getSubCustomizationGroups(option));
      }
      this.selectedCustomizations[key] = selected;
    } else {
      if (this.selectedCustomizations[key] === option && this.isSingleSelectMandatory(group)) {
        this.addonErrors = ['One option is required for ' + this.getCustomizationName(group) + '.'];
        return;
      }
      if (this.selectedCustomizations[key] === option) {
        delete this.selectedCustomizations[key];
        return;
      }
      this.selectedCustomizations[key] = option;
      this.applyDefaultCustomizations(this.getSubCustomizationGroups(option));
    }
  }

  isCustomizationSelected(group: any, option: any): boolean {
    const key = this.getCustomizationKey(group);
    const selected = this.selectedCustomizations[key];
    if (Array.isArray(selected)) {
      return selected.indexOf(option) > -1;
    }
    return selected === option;
  }

  updateTemplateField(field: any, value: any) {
    if (this.isTemplateMultiSelect(field)) {
      field.value = field.value || [];
      const index = field.value.indexOf(value);
      if (index > -1) {
        field.value.splice(index, 1);
      } else {
        field.value.push(value);
      }
    } else {
      field.value = value;
    }
  }

  private restoreProductActionState(actionData?: any, options?: any) {
    if (!actionData) {
      return;
    }
    const preserveLiveSelections = !!(options && options.preserveLiveSelections);
    const merchant = actionData.merchant || actionData.selectedMerchant;
    const product = actionData.product || actionData.selectedProduct;
    if (merchant) {
      this.selectedMerchant = merchant;
      this.selectedProductMerchant = merchant;
      this.lastMerchantContext = merchant;
    }
    if (product) {
      this.selectedProduct = product;
    }
    if (actionData.quantity && !preserveLiveSelections) {
      this.selectedQuantity = Math.max(1, Number(actionData.quantity) || 1);
    }
    if (!preserveLiveSelections && Array.isArray(actionData.customizationGroups)) {
      this.customizationGroups = actionData.customizationGroups;
      this.selectedCustomizations = {};
      this.applyDefaultCustomizations(this.customizationGroups);
    } else if (product && !this.customizationGroups.length) {
      this.customizationGroups = this.normalizeCustomizations(product);
      this.applyDefaultCustomizations(this.customizationGroups);
    }
    if (!preserveLiveSelections && Array.isArray(actionData.templateFields)) {
      this.templateFields = actionData.templateFields;
    } else if (product && !this.templateFields.length) {
      this.templateFields = this.normalizeTemplateFields(product.template);
    }
    if (!preserveLiveSelections || !this.selectedServiceSchedule) {
      this.restoreServiceScheduleState(actionData);
    }
  }

  private restoreLatestActionState(messages: OrderAiMessage[]) {
    const actionableTypes = [
      'product-options',
      'quantity',
      'cart-conflict',
      'service-agent-candidates',
      'service-date-options',
      'service-start-slots',
      'service-end-slots',
      'service-surge-confirm',
      'service-schedule-summary'
    ];
    let actionable = null;
    const reversedMessages = (messages || []).slice().reverse();
    for (let i = 0; i < reversedMessages.length; i++) {
      if (reversedMessages[i].type === 'service-schedule-cancelled') {
        return;
      }
      if (actionableTypes.indexOf(reversedMessages[i].type) > -1) {
        actionable = reversedMessages[i];
        break;
      }
    }
    if (!actionable) {
      return;
    }
    if (actionable.type === 'cart-conflict') {
      this.pendingProductCandidate = actionable.data && actionable.data.candidate;
      return;
    }
    this.restoreProductActionState(actionable.data);
  }

  private shouldPreserveLiveProductActionState(actionData?: any): boolean {
    if (!actionData || !this.selectedProduct) {
      return false;
    }
    const actionProduct = actionData.product || actionData.selectedProduct;
    if (!actionProduct) {
      return false;
    }
    return String(this.getProductId(this.selectedProduct)) === String(this.getProductId(actionProduct));
  }

  addCurrentProductToCart(actionData?: any) {
    this.restoreProductActionState(actionData, {
      preserveLiveSelections: this.shouldPreserveLiveProductActionState(actionData)
    });
    const availabilityMessage = this.getProductAvailabilityMessage(this.selectedProduct, this.selectedProductMerchant || this.selectedMerchant);
    if (availabilityMessage) {
      this.addAiText(availabilityMessage);
      return;
    }
    const quantityError = this.getQuantityValidationMessage(this.selectedProduct, this.selectedQuantity);
    if (quantityError) {
      this.addFollowup(quantityError);
      return;
    }
    const customizationError = this.getCustomizationValidationError();
    if (customizationError) {
      this.addonErrors = [customizationError];
      this.addFollowup(customizationError);
      return;
    }
    const missingTemplate = this.getMissingTemplateField();
    if (missingTemplate) {
      this.addFollowup('Please fill ' + (missingTemplate.display_name || missingTemplate.key || 'the required field') + '.');
      return;
    }
    if (this.shouldScheduleServiceProduct(this.selectedProduct, this.selectedProductMerchant || this.selectedMerchant)) {
      if (!this.selectedServiceSchedule) {
        this.beginServiceScheduling();
        return;
      }
    }
    this.setMerchantSession(this.selectedProductMerchant || this.selectedMerchant);
    const cartItem = this.buildCartItem();
    this.checkoutService.setProductInCart(cartItem);
    this.cartService.updateAddCartStatus();
    this.addAssistantMessage({
      role: 'ai',
      type: 'cart-summary',
      text: 'Added to cart',
      data: {
        product: this.getProductName(this.selectedProduct),
        merchant: this.getMerchantName(this.selectedProductMerchant || this.selectedMerchant),
        quantity: this.selectedQuantity,
        currency: this.getDisplayCurrency(this.selectedProduct, this.selectedProductMerchant || this.selectedMerchant),
        amount: cartItem.totalPrice
      }
    }, {
      selectedMerchant: this.selectedProductMerchant || this.selectedMerchant,
      lastOrderedProduct: this.selectedProduct,
      lastOrderSummary: this.selectedQuantity + ' ' + this.getProductName(this.selectedProduct),
      pendingAction: '',
      pendingProductSearch: '',
      pendingQuantity: ''
    });
    this.lastMerchantContext = this.selectedProductMerchant || this.selectedMerchant || this.lastMerchantContext;
    this.resetSelectionState(false);
  }

  proceedToCheckout() {
    const checkoutError = this.getCheckoutValidationMessage();
    if (checkoutError) {
      this.addFollowup(checkoutError);
      return;
    }
    if (!this.canCheckoutWithoutLogin()) {
      this.isOpen = false;
      this.messageService.openLoginModalBySignup('From Checkout Button');
      return;
    }
    this.setMerchantSession(this.selectedProductMerchant || this.selectedMerchant || this.sessionService.get('info'));
    this.isOpen = false;
    this.router.navigate(['checkout']);
  }

  isComposerDisabled(): boolean {
    const last = this.messages && this.messages.length ? this.messages[this.messages.length - 1] : null;
    return this.isTyping || !this.chatPersistenceReady || !!(last && [
      'merchant-candidates',
      'product-candidates',
      'product-options',
      'quantity',
      'cart-conflict',
      'cart-summary',
      'merchant-actions',
      'service-agent-candidates',
      'service-date-options',
      'service-start-slots',
      'service-end-slots',
      'service-surge-confirm',
      'service-schedule-summary'
    ].indexOf(last.type) > -1);
  }

  isActionMessage(message: OrderAiMessage): boolean {
    return !!(message && this.getActionMessageTypes().indexOf(message.type) > -1);
  }

  isLatestActionMessage(message: OrderAiMessage, messageIndex: number): boolean {
    return !this.isTyping &&
      this.isActionMessage(message) &&
      messageIndex === this.getLatestActionMessageIndex();
  }

  isActionMessageUsable(message: OrderAiMessage, messageIndex: number): boolean {
    return this.isLatestActionMessage(message, messageIndex) ||
      this.isServiceDateOptionsActionable(message, messageIndex);
  }

  isServiceDateOptionsActionable(message: OrderAiMessage, messageIndex: number): boolean {
    if (this.isLatestActionMessage(message, messageIndex)) {
      return true;
    }
    const latestActionIndex = this.getLatestActionMessageIndex();
    const latestAction = latestActionIndex > -1 ? this.messages[latestActionIndex] : null;
    if (this.isTyping || !message || message.type !== 'service-date-options' || !latestAction || messageIndex >= latestActionIndex) {
      return false;
    }
    if (this.isTerminalServiceScheduleAction(latestAction)) {
      return false;
    }
    if (!this.isServiceDateOptionsInActiveSchedulingWindow(messageIndex, latestActionIndex)) {
      return false;
    }
    return latestAction.type === 'followup' ||
      this.isSameServiceScheduleActionContext(message.data, latestAction.data);
  }

  private isServiceDateOptionsInActiveSchedulingWindow(messageIndex: number, latestActionIndex: number): boolean {
    const allowedContinuationTypes = [
      'service-start-slots',
      'service-end-slots',
      'service-surge-confirm',
      'followup'
    ];
    for (let index = messageIndex + 1; index <= latestActionIndex; index++) {
      const message = this.messages[index];
      if (!this.isActionMessage(message)) {
        continue;
      }
      if (this.isTerminalServiceScheduleAction(message)) {
        return false;
      }
      if (allowedContinuationTypes.indexOf(message.type) === -1) {
        return false;
      }
    }
    return true;
  }

  private isTerminalServiceScheduleAction(message: OrderAiMessage): boolean {
    return !!(message && [
      'service-schedule-summary',
      'service-schedule-cancelled',
      'cart-summary',
      'product-candidates',
      'product-options',
      'quantity',
      'cart-conflict',
      'merchant-candidates',
      'merchant-actions',
      'current-store-confirm',
      'service-agent-candidates',
      'service-date-options'
    ].indexOf(message.type) > -1);
  }

  private isSameServiceScheduleActionContext(source: any, target: any): boolean {
    if (!source || !target) {
      return false;
    }
    const sourceProductId = this.getProductId(source.product || source.selectedProduct);
    const targetProductId = this.getProductId(target.product || target.selectedProduct);
    if (sourceProductId && targetProductId && String(sourceProductId) !== String(targetProductId)) {
      return false;
    }
    const sourceMerchantId = this.getMerchantId(source.merchant || source.selectedMerchant);
    const targetMerchantId = this.getMerchantId(target.merchant || target.selectedMerchant);
    if (sourceMerchantId && targetMerchantId && String(sourceMerchantId) !== String(targetMerchantId)) {
      return false;
    }
    const sourceAgentId = this.getServiceScheduleAgentId(source);
    const targetAgentId = this.getServiceScheduleAgentId(target);
    if (sourceAgentId && targetAgentId && String(sourceAgentId) !== String(targetAgentId)) {
      return false;
    }
    return !!(sourceProductId || sourceMerchantId || sourceAgentId);
  }

  private getServiceScheduleAgentId(data: any): any {
    const agent = data && (data.selectedAgent || data.agent);
    return data && (data.agent_id || data.selectedAgentId) ||
      agent && (agent.agent_id || agent.fleet_id || agent.user_id || agent.id);
  }

  loadChatrooms() {
    if (this.isChatroomsLoading) {
      return;
    }
    const payload = this.getChatApiPayload({ action: 'list' });
    if (!this.chatPersistenceReady) {
      this.activeChatroom = null;
      this.activeChatId = '';
      this.chatrooms = [];
      this.transientGreeting = '';
      this.messages = [{
        role: 'ai',
        text: `Please login to use saved ${this.getBrandLabel()} chats.`
      }];
      return;
    }
    this.isChatroomsLoading = true;
    this.orderWithAiService.chatrooms(payload).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.isChatroomsLoading = false;
        const data = this.extractApiData(response);
        this.chatrooms = data && Array.isArray(data.chatrooms) ? data.chatrooms : [];
        if (this.chatrooms.length) {
          const selected = this.activeChatId ?
            this.chatrooms.find(chatroom => chatroom.chatId === this.activeChatId) || this.chatrooms[0] :
            this.chatrooms[0];
          this.selectChatroom(selected);
          return;
        }
        this.createChatroom();
      },
      () => {
        this.isChatroomsLoading = false;
        this.messages = [{
          role: 'ai',
          text: `I could not load your ${this.getBrandLabel()} chats right now.`
        }];
      }
    );
  }

  // Sidebar "New" button. Clears the live in-memory context too, not just the create
  // payload -- shouldShowCurrentStoreSuggestion() (the "you're viewing this store,
  // search here?" banner) refuses to fire while this.selectedMerchant/lastMerchantContext
  // are still set, so a stale value here would silently break that suggestion on a
  // genuinely new chat even though the created chatroom itself is correctly blank.
  startBlankChatroom() {
    this.resetSelectionState();
    this.lastMerchantContext = null;
    this.createChatroom(null);
  }

  // Sidebar "Try asking" tiles -- same blank-chat setup as startBlankChatroom(), then
  // seed and fire the suggestion as the first message once the chatroom is ready.
  startQuickPrompt(text: string) {
    // Re-checked here even though the tile is only rendered while chatPersistenceReady
    // was true at render time, because getChatApiPayload() (called inside createChatroom,
    // further down) recomputes chatPersistenceReady fresh on every call -- it can go
    // stale between render and click without the *ngIf re-evaluating in between.
    if (!this.chatPersistenceReady || this.isTyping) {
      return;
    }
    this.resetSelectionState();
    this.lastMerchantContext = null;
    this.createChatroom(null, () => {
      this.prompt = text;
      this.sendPrompt();
    });
  }

  // selectedMerchant omitted (undefined) -> seed from whatever's currently in context
  // (e.g. the very first chat ever, possibly opened via initialMerchant from a store
  // page). selectedMerchant passed explicitly as null -> force a genuinely blank chat,
  // ignoring leftover this.selectedMerchant/lastMerchantContext from whatever chat was
  // open before. `null || this.selectedMerchant` would silently undo that explicit null
  // since null is falsy in JS -- that was the actual bug behind "New chat" reopening
  // with the previous chat's merchant/title still attached.
  createChatroom(selectedMerchant?: any, onReady?: () => void) {
    const seedMerchant = selectedMerchant !== undefined
      ? selectedMerchant
      : (this.selectedMerchant || this.lastMerchantContext || null);
    const payload: any = this.getChatApiPayload({
      action: 'create',
      selectedMerchant: seedMerchant || undefined,
      currentContext: seedMerchant ? { selectedMerchant: seedMerchant } : {}
    });
    // Switched to the chat pane here, before anything below can return early or the API
    // call resolves either way. On mobile the sidebar/chat pane are two separate
    // full-screen views (see mobileChatView), and every path below this -- the
    // not-logged-in early return included -- only ever adds a message to this.messages
    // with no view switch of its own. A tap that hit any of these (e.g. a quick-prompt
    // tile tapped while chatPersistenceReady was stale/false) used to leave that message
    // silently sitting in a pane the user was never shown -- looked exactly like
    // "nothing happened" from a tap, when something did happen, just invisibly.
    this.mobileChatView = 'chat';
    if (!this.chatPersistenceReady) {
      this.messages = [{
        role: 'ai',
        text: `Please login to use saved ${this.getBrandLabel()} chats.`
      }];
      return;
    }
    this.setTyping('Starting chat');
    this.orderWithAiService.chatrooms(payload).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        const data = this.extractApiData(response);
        if (!data || !data.chatroom) {
          this.addAiText('I could not start a new chat right now.');
          return;
        }
        this.upsertChatroom(data.chatroom);
        this.setActiveChatroom(data.chatroom);
        this.messages = data.messages && data.messages.length ? this.normalizeStoredMessages(data.messages) : [this.getStarterMessage()];
        this.restoreLatestActionState(this.messages);
        this.transientGreeting = data.greeting || '';
        this.prepareCurrentStoreSuggestion();
        if (onReady) {
          onReady();
        }
      },
      () => {
        this.clearTyping();
        this.addAiText('I could not start a new chat right now.');
      }
    );
  }

  selectChatroom(chatroom: any) {
    if (!chatroom || !chatroom.chatId || this.isMessagesLoading) {
      return;
    }
    this.mobileChatView = 'chat';
    this.isMessagesLoading = true;
    this.setTyping('Loading chat');
    this.orderWithAiService.chatrooms(this.getChatApiPayload({
      action: 'get',
      chatId: chatroom.chatId
    })).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.isMessagesLoading = false;
        this.clearTyping();
        const data = this.extractApiData(response);
        if (!data || !data.chatroom) {
          this.addAiText('I could not open that chat right now.');
          return;
        }
        this.setActiveChatroom(data.chatroom);
        this.messages = data.messages && data.messages.length ? this.normalizeStoredMessages(data.messages) : [this.getStarterMessage()];
        this.restoreLatestActionState(this.messages);
        this.refreshActiveMerchantFromMessages(this.messages);
        this.transientGreeting = data.greeting || '';
        this.prepareCurrentStoreSuggestion();
      },
      () => {
        this.isMessagesLoading = false;
        this.clearTyping();
        this.addAiText('I could not open that chat right now.');
      }
    );
  }

  renameChatroom(chatroom: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!chatroom || !chatroom.chatId) {
      return;
    }
    this.deleteConfirmChatId = '';
    this.renamingChatId = chatroom.chatId;
    this.renameTitleDraft = this.getChatTitle(chatroom);
  }

  saveChatroomRename(chatroom: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const nextTitle = (this.renameTitleDraft || '').trim();
    if (!nextTitle || !chatroom || !chatroom.chatId) {
      return;
    }
    if (nextTitle === this.getChatTitle(chatroom)) {
      this.cancelChatroomRename(event);
      return;
    }
    this.chatActionPendingId = chatroom.chatId;
    this.orderWithAiService.chatrooms(this.getChatApiPayload({
      action: 'rename',
      chatId: chatroom.chatId,
      title: nextTitle
    })).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(response => {
      this.chatActionPendingId = '';
      this.renamingChatId = '';
      this.renameTitleDraft = '';
      const data = this.extractApiData(response);
      if (data && data.chatroom) {
        this.upsertChatroom(data.chatroom);
        if (this.activeChatId === data.chatroom.chatId) {
          this.setActiveChatroom(data.chatroom, false);
        }
      }
    }, () => {
      this.chatActionPendingId = '';
      this.addAiText('I could not rename that chat right now.');
    });
  }

  cancelChatroomRename(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.renamingChatId = '';
    this.renameTitleDraft = '';
  }

  deleteChatroom(chatroom: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!chatroom || !chatroom.chatId) {
      return;
    }
    this.renamingChatId = '';
    this.renameTitleDraft = '';
    this.deleteConfirmChatId = chatroom.chatId;
  }

  confirmDeleteChatroom(chatroom: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!chatroom || !chatroom.chatId) {
      return;
    }
    this.chatActionPendingId = chatroom.chatId;
    this.orderWithAiService.chatrooms(this.getChatApiPayload({
      action: 'delete',
      chatId: chatroom.chatId
    })).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(() => {
      this.chatActionPendingId = '';
      this.deleteConfirmChatId = '';
      this.chatrooms = this.chatrooms.filter(item => item.chatId !== chatroom.chatId);
      if (this.activeChatId !== chatroom.chatId) {
        return;
      }
      this.activeChatroom = null;
      this.activeChatId = '';
      this.transientGreeting = '';
      if (this.chatrooms.length) {
        this.resetSelectionState();
        this.selectChatroom(this.chatrooms[0]);
      } else {
        this.startBlankChatroom();
      }
    }, () => {
      this.chatActionPendingId = '';
      this.addAiText('I could not delete that chat right now.');
    });
  }

  cancelDeleteChatroom(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.deleteConfirmChatId = '';
  }

  acceptCurrentStoreSuggestion() {
    const merchant = this.currentStoreSuggestion;
    if (!merchant) {
      return;
    }
    this.currentStoreSuggestion = null;
    this.currentStoreSuggestionDismissed = true;
    this.selectedMerchant = merchant;
    this.lastMerchantContext = merchant;
    this.setMerchantSession(merchant);
    this.syncContext({
      selectedMerchant: merchant,
      pendingAction: '',
      pendingProductSearch: ''
    });
    this.transientGreeting = 'This chat is now scoped to ' + this.getMerchantName(merchant) + '. Tell me what you want from this store.';
  }

  dismissCurrentStoreSuggestion() {
    this.currentStoreSuggestion = null;
    this.currentStoreSuggestionDismissed = true;
  }

  getChatTitle(chatroom: any): string {
    return chatroom && chatroom.title || 'New AI Order Chat';
  }

  getChatMerchantTitle(chatroom: any): string {
    const merchant = chatroom && chatroom.currentContext && chatroom.currentContext.selectedMerchant;
    return chatroom && (chatroom.merchantName || (merchant ? this.getMerchantName(merchant) : '')) || '';
  }

  getChatPreview(chatroom: any): string {
    return chatroom && (chatroom.lastAssistantMessage || chatroom.lastUserPrompt || chatroom.lastOrderSummary) || 'No messages yet';
  }

  getChatUpdatedAt(chatroom: any): string {
    if (!chatroom || !chatroom.updatedAt) {
      return '';
    }
    const updated = new Date(chatroom.updatedAt);
    if (!updated.getTime()) {
      return '';
    }
    return updated.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  private handleChatDirective(directive: any, assistantAlreadyShown = false) {
    if (!directive) {
      this.handleLegacyIntent();
      return;
    }
    if (directive.quantity) {
      this.selectedQuantity = Math.max(1, Number(directive.quantity) || 1);
    }
    if (directive.type === 'search_merchants') {
      this.searchMerchants(directive.searchText || directive.merchantSearchText || '');
      return;
    }
    if (directive.type === 'search_products_in_merchant') {
      const merchant = directive.merchant || this.selectedMerchant || this.lastMerchantContext;
      if (merchant) {
        this.selectedMerchant = merchant;
        this.lastMerchantContext = merchant;
        this.searchMerchantProducts(directive.searchText || directive.productSearchText || '', merchant);
        return;
      }
      this.searchProducts(directive.searchText || directive.productSearchText || '');
      return;
    }
    if (directive.type === 'search_global_products') {
      if (this.shouldAskCurrentStoreBeforeGlobalSearch()) {
        this.confirmCurrentStoreProductSearch(directive.searchText || directive.productSearchText || '');
        return;
      }
      this.selectedMerchant = null;
      this.lastMerchantContext = null;
      this.searchProducts(directive.searchText || directive.productSearchText || '');
      return;
    }
    if (directive.type === 'ask_product') {
      if (!assistantAlreadyShown) {
        this.addFollowup(directive.message || 'Which product should I search for?');
      }
      return;
    }
    this.handleLegacyIntent();
  }

  private handleLegacyIntent() {
    const contextMerchant = this.selectedMerchant || this.lastMerchantContext;
    if (this.intent && this.intent.merchantSearchText) {
      this.searchMerchants(this.intent.merchantSearchText);
      return;
    }
    if (this.intent && this.intent.unsupportedReason && !this.intent.productSearchText) {
      this.addAiText(this.intent.unsupportedReason);
      this.addFollowup('Try a product marketplace order like "2 chicken tikka pizzas from Pizzeria by Jamie".');
      return;
    }
    if (!this.intent || !this.intent.productSearchText) {
      this.addFollowup(contextMerchant ? 'Which product should I search from ' + this.getMerchantName(contextMerchant) + '?' : 'Which product should I search for?');
      return;
    }
    if (this.intent.quantity) {
      this.selectedQuantity = Math.max(1, Number(this.intent.quantity) || 1);
    }
    const currentStoreId = this.getCurrentStoreIdFromRoute();
    const contextMerchantId = contextMerchant ? this.getMerchantId(contextMerchant) : null;
    if (currentStoreId && (!contextMerchantId || String(contextMerchantId) !== String(currentStoreId))) {
      this.confirmCurrentStoreProductSearch(this.intent.productSearchText);
      return;
    }
    if (!contextMerchant) {
      this.addFollowup('I can search products now. If you have a preferred store, tell me its name first.');
    }
    this.searchProducts(this.intent.productSearchText);
  }

  private shouldAskCurrentStoreBeforeGlobalSearch(): boolean {
    if (this.currentStoreSuggestionDismissed) {
      return false;
    }
    const contextMerchant = this.selectedMerchant || this.lastMerchantContext;
    const currentStoreId = this.getCurrentStoreIdFromRoute();
    const contextMerchantId = contextMerchant ? this.getMerchantId(contextMerchant) : null;
    return !!(currentStoreId && (!contextMerchantId || String(contextMerchantId) !== String(currentStoreId)));
  }

  private getChatApiPayload(extra?: any): any {
    const ownerPayload = this.getChatOwnerPayload();
    this.chatPersistenceReady = !!(ownerPayload.marketplace_user_id && ownerPayload.vendor_id && ownerPayload.access_token);
    return Object.assign(ownerPayload, extra || {});
  }

  private getChatOwnerPayload(): any {
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    const location = this.getSessionObject('location') || {};
    const marketplaceUserId = config.marketplace_user_id || vendor.marketplace_user_id;
    return {
      marketplace_user_id: marketplaceUserId,
      marketplaceUserId,
      vendor_id: vendor.vendor_id,
      ownerUserId: vendor.vendor_id,
      customer_id: vendor.vendor_id,
      access_token: vendor.app_access_token,
      language: this.getSessionString('language') || 'en',
      domainName: config.domain_name,
      domain_name: config.domain_name,
      latitude: location.latitude || location.lat,
      longitude: location.longitude || location.lng,
      deliveryMethod: this.getSessionObject('self_pickup') || this.getSelfPickupValue(),
      appType: 'WEB',
      app_type: 'WEB',
      dual_user_key: 0
    };
  }

  private extractApiData(response: any): any {
    return response && response.data ? response.data : response;
  }

  private normalizeStoredMessages(messages: any[]): OrderAiMessage[] {
    const normalized = (messages || []).map(message => this.normalizeStoredMessage(message));
    let serviceScheduleCancelled = false;
    for (let i = normalized.length - 1; i >= 0; i--) {
      if (normalized[i].type === 'service-schedule-cancelled') {
        serviceScheduleCancelled = true;
      } else if (serviceScheduleCancelled && normalized[i].type === 'service-surge-confirm') {
        normalized[i] = Object.assign({}, normalized[i], {
          type: 'service-surge-expired',
          text: 'This surge confirmation was cancelled.'
        });
      }
    }
    return normalized;
  }

  private normalizeStoredMessage(message: any): OrderAiMessage {
    return {
      role: message && message.role === 'user' ? 'user' : message && message.role === 'system' ? 'system' : 'ai',
      text: message && (message.content || message.text) || '',
      type: message && (message.messageType || message.type) || 'text',
      data: message && (message.structuredPayload || message.data) || {},
      messageId: message && (message.messageId || message._id)
    };
  }

  private normalizeMessageForApi(message: OrderAiMessage): any {
    return {
      role: message.role === 'user' ? 'user' : message.role === 'system' ? 'system' : 'assistant',
      content: message.text || '',
      messageType: message.type || 'text',
      structuredPayload: message.data || {},
      messageId: message.messageId
    };
  }

  private setActiveChatroom(chatroom: any, applyContext = true) {
    this.activeChatroom = chatroom;
    this.activeChatId = chatroom && chatroom.chatId || '';
    this.upsertChatroom(chatroom);
    if (applyContext) {
      this.applyChatroomContext(chatroom);
    }
  }

  // WhatsApp-style: mobile shows either the chat list or one open chat full-screen,
  // never both at once like desktop's side-by-side layout. Back button in the mobile
  // header calls this to return to the list without touching activeChatroom/activeChatId
  // at all -- the chat stays open underneath, same as backing out of a WhatsApp thread.
  showMobileChatList() {
    this.mobileChatView = 'list';
  }

  private upsertChatroom(chatroom: any) {
    if (!chatroom || !chatroom.chatId) {
      return;
    }
    const existingIndex = this.chatrooms.findIndex(item => item.chatId === chatroom.chatId);
    if (existingIndex > -1) {
      this.chatrooms[existingIndex] = chatroom;
    } else {
      this.chatrooms.unshift(chatroom);
    }
    this.chatrooms = this.chatrooms.slice().sort((a, b) => {
      const aTime = a && a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b && b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime;
    });
  }

  private applyChatroomContext(chatroom: any) {
    const context = chatroom && (chatroom.currentContext || chatroom.current_context || chatroom.metadata) || {};
    const merchant = context.selectedMerchant || context.selected_merchant || this.getChatroomMerchantFallback(chatroom);
    this.resetSelectionState();
    if (merchant) {
      const normalizedMerchant = this.buildMerchantSessionContext(merchant) || merchant;
      this.selectedMerchant = normalizedMerchant;
      this.lastMerchantContext = normalizedMerchant;
      this.setMerchantSession(normalizedMerchant);
      this.refreshChatroomMerchantContext(chatroom, normalizedMerchant);
    } else {
      this.lastMerchantContext = null;
    }
    if (context.pendingQuantity || chatroom && chatroom.pendingQuantity) {
      this.selectedQuantity = Math.max(1, Number(context.pendingQuantity || chatroom.pendingQuantity) || 1);
    }
    if (context.selectedServiceSchedule) {
      this.selectedServiceSchedule = context.selectedServiceSchedule;
    }
    if (context.pendingServiceSchedule) {
      this.pendingServiceSchedule = context.pendingServiceSchedule;
    }
  }

  private refreshChatroomMerchantContext(chatroom: any, merchant: any) {
    const chatId = chatroom && chatroom.chatId;
    if (!chatId || !merchant || !this.getMerchantId(merchant)) {
      return;
    }
    this.refreshMerchantDetails(merchant, refreshedMerchant => {
      if (this.activeChatId !== chatId) {
        return;
      }
      this.applyRefreshedMerchantContext(refreshedMerchant, true);
    });
  }

  private getChatroomMerchantFallback(chatroom: any): any {
    const merchantId = chatroom && (
      chatroom.merchantId ||
      chatroom.merchant_id ||
      chatroom.storefrontUserId ||
      chatroom.storefront_user_id ||
      chatroom.user_id ||
      chatroom.storeId ||
      chatroom.store_id
    );
    if (!merchantId) {
      return null;
    }
    return {
      storefront_user_id: merchantId,
      user_id: merchantId,
      name: chatroom.merchantName || chatroom.title,
      business_name: chatroom.merchantName || chatroom.title,
      merchant_slug: chatroom.merchantSlug || chatroom.merchant_slug
    };
  }

  private refreshActiveMerchantFromMessages(messages: OrderAiMessage[]) {
    const messageMerchant = this.findMerchantInMessages(messages);
    if (!messageMerchant || !this.getMerchantId(messageMerchant)) {
      return;
    }
    const selectedId = this.getMerchantId(this.selectedMerchant);
    const messageMerchantId = this.getMerchantId(messageMerchant);
    if (selectedId && String(selectedId) !== String(messageMerchantId)) {
      return;
    }
    const mergedMerchant = this.buildMerchantSessionContext(Object.assign({}, this.selectedMerchant || {}, messageMerchant)) || messageMerchant;
    this.selectedMerchant = mergedMerchant;
    this.lastMerchantContext = mergedMerchant;
    this.refreshChatroomMerchantContext(this.activeChatroom, mergedMerchant);
  }

  private findMerchantInMessages(messages: OrderAiMessage[]): any {
    const chatroomMerchant = this.getChatroomMerchantFallback(this.activeChatroom);
    const reversed = (messages || []).slice().reverse();
    for (let i = 0; i < reversed.length; i++) {
      const data = reversed[i] && reversed[i].data;
      const merchant = this.extractMerchantFromPayload(data);
      if (merchant && this.getMerchantId(merchant)) {
        return Object.assign({}, chatroomMerchant || {}, merchant);
      }
    }
    return chatroomMerchant;
  }

  private extractMerchantFromPayload(payload: any): any {
    if (!payload) {
      return null;
    }
    if (Array.isArray(payload)) {
      for (let i = 0; i < payload.length; i++) {
        const merchant = this.extractMerchantFromPayload(payload[i]);
        if (merchant && this.getMerchantId(merchant)) {
          return merchant;
        }
      }
      return null;
    }
    const directMerchant = payload.selectedMerchant || payload.selected_merchant || payload.merchant || payload.selectedProductMerchant || payload.selected_product_merchant;
    if (directMerchant && this.getMerchantId(directMerchant)) {
      return directMerchant;
    }
    if (payload.candidate) {
      const candidateMerchant = this.extractMerchantFromPayload(payload.candidate);
      if (candidateMerchant) {
        return candidateMerchant;
      }
    }
    return null;
  }

  private refreshMerchantDetails(merchant: any, onSuccess: (merchant: any) => void, onFailure?: () => void) {
    const merchantId = this.getMerchantId(merchant);
    if (!merchantId) {
      if (onFailure) {
        onFailure();
      }
      return;
    }
    this.catalogueService.getSingleRestaturant(this.buildSingleStorePayload(String(merchantId))).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        if (this.isApiError(response)) {
          if (onFailure) {
            onFailure();
          }
          return;
        }
        const freshMerchant = this.extractSingleMerchant(response);
        if (!freshMerchant) {
          if (onFailure) {
            onFailure();
          }
          return;
        }
        const freshMerchantContext = Object.assign({}, merchant, freshMerchant);
        this.refreshedMerchantIds[String(merchantId)] = true;
        this.refreshedMerchantContexts[String(merchantId)] = freshMerchantContext;
        const normalizedMerchant = this.buildMerchantSessionContext(freshMerchantContext) || freshMerchantContext;
        this.refreshedMerchantContexts[String(merchantId)] = normalizedMerchant;
        this.applyRefreshedMerchantContext(normalizedMerchant, false);
        if (onSuccess) {
          onSuccess(normalizedMerchant);
        }
      },
      () => {
        if (onFailure) {
          onFailure();
        }
      }
    );
  }

  private applyRefreshedMerchantContext(merchant: any, persistContext: boolean) {
    if (!merchant) {
      return;
    }
    const normalizedMerchant = this.setMerchantSession(merchant, this.selectedProduct) || merchant;
    this.selectedMerchant = normalizedMerchant;
    this.lastMerchantContext = normalizedMerchant;
    if (!this.selectedProductMerchant || this.isSameStoreContext(this.selectedProductMerchant, normalizedMerchant)) {
      this.selectedProductMerchant = normalizedMerchant;
    }
    if (this.activeChatroom) {
      this.activeChatroom = Object.assign({}, this.activeChatroom, {
        merchantId: this.getMerchantId(normalizedMerchant),
        merchantName: this.getMerchantName(normalizedMerchant),
        currentContext: Object.assign({}, this.activeChatroom.currentContext || {}, {
          selectedMerchant: normalizedMerchant
        })
      });
      this.upsertChatroom(this.activeChatroom);
    }
    if (persistContext) {
      this.syncContext({
        selectedMerchant: normalizedMerchant
      });
    }
  }

  private shouldRefreshMerchantContext(merchant: any): boolean {
    const merchantId = this.getMerchantId(merchant);
    if (!merchantId) {
      return false;
    }
    return !this.refreshedMerchantIds[String(merchantId)] ||
      this.isMissingValue(merchant.business_type) ||
      this.isMissingValue(merchant.pd_or_appointment);
  }

  private extractSingleMerchant(response: any): any {
    const data = response && response.data ? response.data : response;
    if (Array.isArray(data)) {
      return data[0] || null;
    }
    if (data && Array.isArray(data.result)) {
      return data.result[0] || null;
    }
    if (data && Array.isArray(data.stores)) {
      return data.stores[0] || null;
    }
    if (data && Array.isArray(data.storefronts)) {
      return data.storefronts[0] || null;
    }
    if (data && (data.storefront_user_id || data.user_id || data.merchant_id || data.name)) {
      return data;
    }
    return null;
  }

  private pushLocalUserMessage(text: string) {
    this.messages.push({ role: 'user', text });
    this.scrollMessagesToBottom();
  }

  private blurPromptInput() {
    try {
      const input = this.elementRef && this.elementRef.nativeElement ?
        this.elementRef.nativeElement.querySelector('.owai-composer input') : null;
      if (input && input.blur) {
        input.blur();
      }
    } catch (error) {}
  }

  private addAssistantMessage(message: OrderAiMessage, contextPatch?: any) {
    this.messages.push(message);
    this.scrollMessagesToBottom();
    this.persistMessages([message], contextPatch);
  }

  private scrollMessagesToBottom() {
    setTimeout(() => {
      try {
        const container = this.elementRef && this.elementRef.nativeElement ?
          this.elementRef.nativeElement.querySelector('.owai-messages') : null;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      } catch (error) {}
    }, 0);
  }

  private persistMessages(messages: OrderAiMessage[], contextPatch?: any) {
    if (!this.chatPersistenceReady || !this.activeChatId || !messages || !messages.length) {
      return;
    }
    this.orderWithAiService.appendMessages(this.getChatApiPayload({
      chatId: this.activeChatId,
      messages: messages.map(message => this.normalizeMessageForApi(message)),
      contextPatch: contextPatch || {}
    })).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(response => {
      const data = this.extractApiData(response);
      if (data && data.chatroom) {
        this.setActiveChatroom(data.chatroom, false);
      }
    });
  }

  private syncContext(contextPatch: any) {
    if (!this.chatPersistenceReady || !this.activeChatId) {
      return;
    }
    this.orderWithAiService.updateContext(this.getChatApiPayload({
      chatId: this.activeChatId,
      contextPatch: contextPatch || {}
    })).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(response => {
      const data = this.extractApiData(response);
      if (data && data.chatroom) {
        this.setActiveChatroom(data.chatroom, false);
      }
    });
  }

  private prepareCurrentStoreSuggestion() {
    this.currentStoreSuggestion = null;
    this.currentStoreSuggestionDismissed = false;
    if (!this.shouldShowCurrentStoreSuggestion()) {
      return;
    }
    const currentStore = this.getCurrentStoreContext();
    if (currentStore) {
      this.currentStoreSuggestion = currentStore;
      return;
    }
    const storeId = this.getCurrentStoreIdFromRoute();
    if (!storeId) {
      return;
    }
    this.catalogueService.getSingleRestaturant(this.buildSingleStorePayload(storeId)).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        if (!this.shouldShowCurrentStoreSuggestion() || this.isApiError(response)) {
          return;
        }
        const data = response && response.data;
        const merchant = Array.isArray(data) ? data[0] : data;
        if (merchant) {
          this.currentStoreSuggestion = merchant;
        }
      }
    );
  }

  private shouldShowCurrentStoreSuggestion(): boolean {
    if (!this.activeChatroom || this.currentStoreSuggestionDismissed || !this.getCurrentStoreIdFromRoute()) {
      return false;
    }
    const context = this.activeChatroom.currentContext || {};
    if (context.selectedMerchant || context.currentMerchant || this.selectedMerchant || this.lastMerchantContext) {
      return false;
    }
    return this.isFreshStarterChat();
  }

  private isFreshStarterChat(): boolean {
    if (!this.messages || !this.messages.length) {
      return true;
    }
    if (this.messages.length > 1) {
      return false;
    }
    const only = this.messages[0];
    return !only.messageId && only.role !== 'user' && only.text === this.getStarterMessage().text;
  }

  private getStarterMessage(): OrderAiMessage {
    return {
      role: 'ai',
      text: 'Tell me what you want to order. I will find the store and product, then add it to your cart.'
    };
  }

  private handlePendingCurrentStoreReply(text: string): boolean {
    if (!this.pendingCurrentStoreSearch) {
      return false;
    }
    const normalized = (text || '').trim().toLowerCase();
    if (/^(yes|y|yeah|yep|ok|okay|sure)\b/.test(normalized) || normalized.indexOf('search here') > -1 || normalized.indexOf('this store') > -1 || normalized.indexOf('go ahead') > -1) {
      this.searchPendingCurrentStoreProducts();
      return true;
    }
    if (/^(no|n|nope)\b/.test(normalized) || normalized.indexOf('search all') > -1 || normalized.indexOf('all stores') > -1 || normalized.indexOf('everywhere') > -1) {
      this.searchPendingProductEverywhere();
      return true;
    }
    return false;
  }

  private confirmCurrentStoreProductSearch(searchText: string) {
    const currentStore = this.getCurrentStoreContext();
    if (currentStore) {
      this.askToSearchCurrentStore(currentStore, searchText);
      return;
    }
    this.fetchCurrentStoreFromRoute(searchText);
  }

  private askToSearchCurrentStore(merchant: any, searchText: string) {
    this.currentStoreSuggestion = null;
    this.pendingCurrentStoreSearch = { merchant, searchText };
    this.selectedMerchant = merchant;
    this.lastMerchantContext = merchant;
    this.addAssistantMessage({
      role: 'ai',
      type: 'current-store-confirm',
      text: 'You are viewing ' + this.getMerchantName(merchant) + '. Do you want me to look for "' + searchText + '" in this store?',
      data: { merchant, searchText }
    }, {
      selectedMerchant: merchant,
      pendingAction: 'current-store-confirm',
      pendingProductSearch: searchText
    });
  }

  private searchMerchants(searchText: string) {
    const candidates = this.getMerchantSearchCandidates(searchText);
    if (!candidates.length) {
      this.addFollowup('Tell me which store you want to order from.');
      return;
    }
    this.searchMerchantCandidate(candidates, 0, searchText);
  }

  private searchMerchantCandidate(candidates: string[], index: number, originalSearchText: string) {
    const currentSearchText = candidates[index] || originalSearchText;
    this.setTyping('Searching stores');
    const payload = this.buildMarketplacePayload(currentSearchText, false);
    this.searchAllService.getSearchedMerchant(payload).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          if (this.intent && this.intent.productSearchText) {
            this.addAiText('Store search had an issue, so I am searching matching products instead.');
            this.searchProducts(this.intent.productSearchText);
          } else {
            this.addAiText((response && response.message) || 'Store search failed. Please try again.');
          }
          return;
        }
        const merchants = this.normalizeMerchants(response);
        this.merchantCandidates = merchants.slice(0, 5);
        if (!this.merchantCandidates.length) {
          if (index + 1 < candidates.length) {
            this.searchMerchantCandidate(candidates, index + 1, originalSearchText);
            return;
          }
          this.addFollowup('I could not find that store. Try another store name or search by product only.');
          return;
        }
        this.addAssistantMessage({
          role: 'ai',
          type: 'merchant-candidates',
          text: 'Choose the store',
          data: this.merchantCandidates
        }, {
          pendingAction: 'merchant-selection',
          pendingMerchantSelection: originalSearchText,
          pendingProductSearch: this.intent && this.intent.productSearchText || ''
        });
      },
      () => {
        this.clearTyping();
        this.addAiText('Store search failed. Please try again.');
      }
    );
  }

  private getMerchantSearchCandidates(searchText: string): string[] {
    const raw = String(searchText || '').replace(/\s+/g, ' ').trim();
    if (!raw) {
      return [];
    }
    const normalized = raw.replace(/\s+(merchant|store|salon|shop|restaurant|vendor|outlet|parlour|parlor)\s*$/i, '').trim();
    if (!normalized || normalized.toLowerCase() === raw.toLowerCase()) {
      return [raw];
    }
    return [normalized, raw];
  }

  private searchProducts(searchText: string) {
    if (this.selectedMerchant) {
      this.searchMerchantProducts(searchText, this.selectedMerchant);
      return;
    }
    this.setTyping('Searching products');
    const payload = this.buildMarketplacePayload(searchText, true);
    this.searchAllService.getSearchedProduct(payload).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          this.addAiText((response && response.message) || 'Product search failed. Please try again.');
          return;
        }
        const products = this.normalizeProducts(response);
        this.productCandidates = products.slice(0, 6);
        if (!this.productCandidates.length) {
          this.addFollowup('I could not find that product. Try another product name.');
          return;
        }
        this.addAssistantMessage({
          role: 'ai',
          type: 'product-candidates',
          text: 'Choose the product',
          data: this.productCandidates
        }, {
          pendingAction: 'product-candidates',
          pendingProductSearch: searchText
        });
      },
      () => {
        this.clearTyping();
        this.addAiText('Product search failed. Please try again.');
      }
    );
  }

  private searchMerchantProducts(searchText: string, merchant: any, allowMerchantRefresh = true) {
    if (allowMerchantRefresh && this.shouldRefreshMerchantContext(merchant)) {
      this.setTyping('Checking store');
      this.refreshMerchantDetails(merchant,
        refreshedMerchant => this.searchMerchantProducts(searchText, refreshedMerchant, false),
        () => this.searchMerchantProducts(searchText, merchant, false)
      );
      return;
    }
    this.setTyping('Searching products');
    const merchantContext = this.buildMerchantSessionContext(merchant) || merchant;
    this.selectedMerchant = merchantContext;
    this.lastMerchantContext = merchantContext;
    const normalizedSearchText = this.getMerchantScopedProductSearchText(searchText, merchantContext);
    const payload = this.buildMerchantProductSearchPayload(normalizedSearchText, merchantContext);
    this.searchAllService.getMerchantSearchedProducts(payload).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          this.addAiText((response && response.message) || 'Product search failed. Please try again.');
          return;
        }
        const products = this.normalizeMerchantProducts(response, merchantContext);
        this.productCandidates = products.slice(0, 8);
        if (!this.productCandidates.length) {
          this.addFollowup('I could not find that product in ' + this.getMerchantName(merchantContext) + '. Try another product name.');
          return;
        }
        this.addAssistantMessage({
          role: 'ai',
          type: 'product-candidates',
          text: 'Choose a product from ' + this.getMerchantName(merchantContext),
          data: this.productCandidates
        }, {
          selectedMerchant: merchantContext,
          pendingAction: 'product-candidates',
          pendingProductSearch: normalizedSearchText
        });
      },
      () => {
        this.clearTyping();
        this.addAiText('Product search failed. Please try again.');
      }
    );
  }

  private getMerchantScopedProductSearchText(searchText: string, merchant: any): string {
    const original = (searchText || '').trim();
    let normalized = original;
    const merchantName = this.getMerchantName(merchant);
    const fromParts = normalized.split(/\s+from\s+/i);
    if (fromParts.length > 1 && this.isLikelyMerchantText(fromParts.slice(1).join(' from '), merchantName)) {
      normalized = fromParts[0];
    }
    if (merchantName && merchantName !== 'Store') {
      normalized = normalized.replace(new RegExp('\\bfrom\\s+' + this.escapeRegExp(merchantName) + '\\b', 'i'), ' ');
    }
    normalized = normalized
      .replace(/^(let'?s|lets|please|can you|could you|i want to|i wanna|want to)?\s*(order|get|add|book)\s+/i, '')
      .replace(/\s+/g, ' ')
      .trim();
    return normalized || original;
  }

  private getReorderProductSearchText(prompt: string): string {
    const match = /^\s*(re[-\s]?order|order\s+again)\b/i.exec(prompt || '');
    if (!match) {
      return '';
    }
    const explicit = String(prompt || '')
      .slice(match[0].length)
      .replace(/^(the|my|same)\s+/i, '')
      .trim();
    if (explicit && !/^(same|it|that)$/i.test(explicit)) {
      return explicit;
    }

    const context = (this.activeChatroom && this.activeChatroom.currentContext) || {};
    const previousIntent = context.previousUserIntent || {};
    const lastOrderSummary = context.lastOrderSummary ||
      (this.activeChatroom && this.activeChatroom.lastOrderSummary);
    return this.firstNonEmpty([
      this.getProductSearchName(context.selectedProduct),
      this.getProductSearchName(context.lastOrderedProduct),
      context.lastOrderedProductName,
      context.lastSearchedProduct,
      previousIntent.productSearchText,
      this.stripProductTextFromSummary(lastOrderSummary)
    ]);
  }

  private getProductSearchName(product: any): string {
    return product && (
      product.productName ||
      product.product_name ||
      product.name ||
      product.item_name
    ) || '';
  }

  private stripProductTextFromSummary(summary: any): string {
    return String(summary || '')
      .replace(/^\d+(\.\d+)?\s+/, '')
      .replace(/\s+from\s+.+$/i, '')
      .trim();
  }

  private firstNonEmpty(values: any[]): string {
    for (let i = 0; i < values.length; i++) {
      const text = values[i] === undefined || values[i] === null ? '' : String(values[i]).trim();
      if (text && text !== 'null') {
        return text;
      }
    }
    return '';
  }

  private isLikelyMerchantText(value: string, merchantName: string): boolean {
    const normalizedValue = this.normalizeSearchTokenText(value);
    const merchantTokens = this.normalizeSearchTokenText(merchantName).split(' ').filter(token => token.length > 2);
    if (!normalizedValue || !merchantTokens.length) {
      return false;
    }
    const matchedTokens = merchantTokens.filter(token => normalizedValue.indexOf(token) > -1).length;
    return matchedTokens >= Math.min(2, merchantTokens.length);
  }

  private normalizeSearchTokenText(value: string): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  private escapeRegExp(value: string): string {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private fetchMerchantProducts(merchant: any, allowMerchantRefresh = true) {
    if (allowMerchantRefresh && this.shouldRefreshMerchantContext(merchant)) {
      this.setTyping('Checking store');
      this.refreshMerchantDetails(merchant,
        refreshedMerchant => this.fetchMerchantProducts(refreshedMerchant, false),
        () => this.fetchMerchantProducts(merchant, false)
      );
      return;
    }
    this.setTyping('Loading products');
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    const payload: any = {
      limit: 50,
      offset: 0,
      marketplace_reference_id: config.marketplace_reference_id,
      marketplace_user_id: config.marketplace_user_id,
      user_id: this.getMerchantId(merchant),
      date_time: new Date().toISOString()
    };
    if (vendor.vendor_id) {
      payload.vendor_id = vendor.vendor_id;
      payload.access_token = vendor.app_access_token;
    }
    this.catalogueService.getProduct(payload).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          this.addAiText((response && response.message) || 'Product list failed. Please try again.');
          return;
        }
        const products = response && Array.isArray(response.data) ? response.data.map(product => ({ product, merchant })) : [];
        if (!products.length) {
          this.addFollowup('No products are available from ' + this.getMerchantName(merchant) + ' right now.');
          return;
        }
        this.productCandidates = products.slice(0, 12);
        this.addAssistantMessage({
          role: 'ai',
          type: 'product-candidates',
          text: 'Choose a product from ' + this.getMerchantName(merchant),
          data: this.productCandidates
        }, {
          selectedMerchant: merchant,
          pendingAction: 'product-candidates'
        });
      },
      () => {
        this.clearTyping();
        this.addAiText('Product list failed. Please try again.');
      }
    );
  }

  private prepareProductOptions() {
    this.customizationGroups = this.normalizeCustomizations(this.selectedProduct);
    this.selectedCustomizations = {};
    this.applyDefaultCustomizations(this.customizationGroups);
    this.templateFields = this.normalizeTemplateFields(this.selectedProduct && this.selectedProduct.template);
    if (this.shouldFetchDeliverectCustomization()) {
      this.setTyping('Loading product options');
      this.searchAllService.getCustomizationData(this.selectedProduct, this.buildDeliverectPayload()).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
        response => {
          this.clearTyping();
          const product = response && response.data ? response.data : response;
          if (product) {
            this.selectedProduct = Object.assign({}, this.selectedProduct, product);
            this.customizationGroups = this.normalizeCustomizations(this.selectedProduct);
            this.selectedCustomizations = {};
            this.applyDefaultCustomizations(this.customizationGroups);
          }
          this.askForOptionsOrAdd();
        },
        () => {
          this.clearTyping();
          this.askForOptionsOrAdd();
        }
      );
      return;
    }
    if (this.selectedProduct && Number(this.selectedProduct.is_product_template_enabled) === 1 && !this.templateFields.length) {
      this.setTyping('Loading product questions');
      this.productTemplateService.getProductTemplate(this.selectedProduct.product_id || this.selectedProduct.id).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
        (response: any) => {
          this.clearTyping();
          const data = response && response.data ? response.data : response;
          this.templateFields = this.normalizeTemplateFields(data && (data.template || data.result || data));
          this.askForOptionsOrAdd();
        },
        () => {
          this.clearTyping();
          this.askForOptionsOrAdd();
        }
      );
      return;
    }
    this.askForOptionsOrAdd();
  }

  private askForOptionsOrAdd() {
    if (this.customizationGroups.length || this.templateFields.length) {
      this.addAssistantMessage({
        role: 'ai',
        type: 'product-options',
        text: 'Choose product options',
        data: this.getProductActionPayload()
      }, {
        selectedMerchant: this.selectedProductMerchant || this.selectedMerchant,
        selectedProduct: this.selectedProduct,
        pendingAction: 'product-options',
        pendingQuantity: this.selectedQuantity
      });
      return;
    }
    if (this.selectedServiceSchedule && this.shouldScheduleServiceProduct(this.selectedProduct, this.selectedProductMerchant || this.selectedMerchant)) {
      this.showExistingServiceScheduleSummary(this.selectedServiceSchedule);
      return;
    }
    this.addAssistantMessage({
      role: 'ai',
      type: 'quantity',
      text: 'Confirm quantity',
      data: this.getProductActionPayload()
    }, {
      selectedMerchant: this.selectedProductMerchant || this.selectedMerchant,
      selectedProduct: this.selectedProduct,
      pendingAction: 'quantity',
      pendingQuantity: this.selectedQuantity
    });
  }

  private getProductActionPayload(): any {
    return {
      product: this.selectedProduct,
      merchant: this.selectedProductMerchant || this.selectedMerchant,
      quantity: this.selectedQuantity,
      customizationGroups: this.customizationGroups,
      templateFields: this.templateFields,
      selectedServiceSchedule: this.selectedServiceSchedule
    };
  }

  private showExistingServiceScheduleSummary(schedule: any) {
    const product = this.selectedProduct;
    const merchant = this.selectedProductMerchant || this.selectedMerchant;
    if (!product || !merchant || !schedule) {
      this.addFollowup('Please choose the service time again.');
      return;
    }
    const normalizedSchedule = Object.assign({}, schedule, {
      label: schedule.label || this.formatServiceSlotRange(schedule.start_time, schedule.end_time),
      selectedDate: schedule.selectedDate || (schedule.start_time ? this.formatServiceDate(new Date(schedule.start_time)) : '')
    });
    this.selectedServiceSchedule = normalizedSchedule;
    const state = this.buildServiceScheduleState({
      mode: this.getServiceSchedulingMode(product, merchant),
      product,
      merchant,
      quantity: this.selectedQuantity,
      selectedDate: normalizedSchedule.selectedDate,
      start_time: normalizedSchedule.start_time,
      end_time: normalizedSchedule.end_time,
      unit_count: normalizedSchedule.unit_count || 1,
      schedule: normalizedSchedule,
      selectedServiceSchedule: normalizedSchedule
    });
    this.pendingServiceSchedule = state;
    this.scheduleSlideValue = 0;
    this.scheduleSlideCompleting = false;
    this.addAssistantMessage({
      role: 'ai',
      type: 'service-schedule-summary',
      text: 'Service time selected',
      data: state
    }, this.getServiceScheduleContextPatch(state, 'service_schedule_selected'));
  }

  private beginServiceScheduling() {
    const product = this.selectedProduct;
    const merchant = this.selectedProductMerchant || this.selectedMerchant;
    if (!product || !merchant) {
      this.prepareProductOptions();
      return;
    }
    const mode = this.getServiceSchedulingMode(product, merchant);
    if (mode === 'none') {
      this.prepareProductOptions();
      return;
    }
    const state = this.buildServiceScheduleState({
      mode,
      product,
      merchant,
      quantity: this.selectedQuantity,
      dateOptions: this.getServiceDateOptions()
    });
    this.pendingServiceSchedule = state;
    this.syncServiceScheduleContext(state, 'service_scheduling');
    if (mode === 'agent_first') {
      this.fetchServiceAgents(state);
      return;
    }
    this.showServiceDateOptions(state);
  }

  private fetchServiceAgents(state: any) {
    this.setTyping('Loading agents');
    this.orderWithAiService.getAgents(this.buildServiceAgentsPayload(state)).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          this.addAiText((response && response.message) || 'I could not load agents for this service.');
          return;
        }
        const agents = this.normalizeAgents(response);
        if (!agents.length) {
          this.addFollowup('No agents are available for ' + this.getProductName(state.product) + ' right now.');
          return;
        }
        state.agents = agents;
        this.serviceAgents = agents;
        this.pendingServiceSchedule = state;
        this.addAssistantMessage({
          role: 'ai',
          type: 'service-agent-candidates',
          text: 'Choose an agent for ' + this.getProductName(state.product),
          data: state
        }, this.getServiceScheduleContextPatch(state, 'service_scheduling'));
      },
      () => {
        this.clearTyping();
        this.addAiText('I could not load agents for this service.');
      }
    );
  }

  selectServiceAgent(agent: any, actionData?: any) {
    const state = this.restoreServiceActionState(actionData);
    state.selectedAgent = agent;
    if (state.product && agent && agent.tag) {
      state.product.tag = agent.tag;
      state.product.agent_tag = agent.tag;
    }
    this.pendingServiceSchedule = state;
    this.addAssistantMessage({
      role: 'ai',
      type: 'selected',
      text: 'Agent selected: ' + this.getAgentName(agent)
    }, this.getServiceScheduleContextPatch(state, 'service_scheduling'));
    this.showServiceDateOptions(state);
  }

  private showServiceDateOptions(state: any) {
    state.dateOptions = state.dateOptions && state.dateOptions.length ? state.dateOptions : this.getServiceDateOptions();
    state.dateOptions = this.applyServiceDateAvailability(state);
    this.pendingServiceSchedule = state;
    this.addAssistantMessage({
      role: 'ai',
      type: 'service-date-options',
      text: 'Choose a service date',
      data: state
    }, this.getServiceScheduleContextPatch(state, 'service_scheduling'));
  }

  selectServiceDate(dateValue: string, actionData?: any) {
    const state = this.restoreServiceActionState(actionData);
    state.selectedDate = dateValue;
    state.slotStatusMessage = '';
    state.slots = [];
    state.endSlots = [];
    state.dateOptions = this.applyServiceDateAvailability(state);
    this.pendingServiceSchedule = state;
    this.refreshServiceDateOptionsMessage(state);
    this.fetchServiceSlots(state, 'start');
  }

  private fetchServiceSlots(state: any, slotStage: 'start' | 'end') {
    this.setTyping(slotStage === 'start' ? 'Loading service slots' : 'Loading end slots');
    this.orderWithAiService.getProductTimeSlots(this.buildServiceSlotsPayload(state)).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          this.addAiText((response && response.message) || 'I could not load service slots.');
          return;
        }
        const data = this.extractApiData(response);
        if (data && data.date) {
          state.selectedDate = data.date;
        }
        const slots = this.normalizeServiceSlots(data, state);
        if (!slots.length) {
          this.markServiceDateAvailability(state, state.selectedDate, false);
          state.slotStatusMessage = 'No slots are available for ' + this.getServiceDateLabel(state.selectedDate) + '. Choose another date above.';
          this.pendingServiceSchedule = state;
          this.refreshServiceDateOptionsMessage(state);
          this.syncServiceScheduleContext(state, 'service_scheduling');
          return;
        }
        this.markServiceDateAvailability(state, state.selectedDate, true);
        state.slotStatusMessage = '';
        state.slots = slots;
        this.pendingServiceSchedule = state;
        this.refreshServiceDateOptionsMessage(state);
        if (slotStage === 'end') {
          const endSlots = this.getServiceEndSlots(state, slots);
          if (!endSlots.length) {
            this.addFollowup('No end slots are available after that start time.');
            return;
          }
          state.endSlots = endSlots;
          this.serviceEndSlots = endSlots;
          this.addAssistantMessage({
            role: 'ai',
            type: 'service-end-slots',
            text: 'Choose an end time',
            data: state
          }, this.getServiceScheduleContextPatch(state, 'service_scheduling'));
          return;
        }
        this.serviceStartSlots = slots;
        this.addAssistantMessage({
          role: 'ai',
          type: 'service-start-slots',
          text: 'Choose a start time',
          data: state
        }, this.getServiceScheduleContextPatch(state, 'service_scheduling'));
      },
      () => {
        this.clearTyping();
        this.addAiText('I could not load service slots.');
      }
    );
  }

  selectServiceStartSlot(slot: any, actionData?: any) {
    const state = this.restoreServiceActionState(actionData);
    state.selectedStartSlot = slot;
    state.start_time = slot && slot.start_time;
    if (!state.start_time) {
      this.addFollowup('Please choose a valid start slot.');
      return;
    }
    if (this.hasFixedServiceDuration(state.product)) {
      state.end_time = this.addMinutesToIso(state.start_time, Number(state.product.service_time) || 0);
      this.validateServiceSchedule(state);
      return;
    }
    const endSlots = this.getServiceEndSlots(state, state.slots || []);
    if (!endSlots.length) {
      this.fetchServiceSlots(state, 'end');
      return;
    }
    state.endSlots = endSlots;
    this.serviceEndSlots = endSlots;
    this.pendingServiceSchedule = state;
    this.addAssistantMessage({
      role: 'ai',
      type: 'service-end-slots',
      text: 'Choose an end time',
      data: state
    }, this.getServiceScheduleContextPatch(state, 'service_scheduling'));
  }

  selectServiceEndSlot(slot: any, actionData?: any) {
    const state = this.restoreServiceActionState(actionData);
    state.selectedEndSlot = slot;
    state.end_time = slot && (slot.endSelectionTime || slot.start_time || slot.end_time);
    if (!state.start_time || !state.end_time) {
      this.addFollowup('Please choose a valid start and end time.');
      return;
    }
    this.validateServiceSchedule(state);
  }

  private validateServiceSchedule(state: any) {
    const localError = this.getServiceScheduleValidationError(state);
    if (localError) {
      this.addFollowup(localError);
      return;
    }
    const unitResult = this.getServiceUnitCount(state.product, state.start_time, state.end_time);
    if (unitResult.error) {
      this.addFollowup(unitResult.error);
      return;
    }
    state.unit_count = unitResult.unitCount;
    this.setTyping('Checking availability');
    this.orderWithAiService.checkTimeSlots(this.buildServiceAvailabilityPayload(state)).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          this.addAiText((response && response.message) || 'That service slot is not available.');
          return;
        }
        const data = this.extractApiData(response) || {};
        const schedule = this.buildSelectedServiceSchedule(state, data.surge_amount);
        if (data.surge_amount) {
          this.addAssistantMessage({
            role: 'ai',
            type: 'service-surge-confirm',
            text: 'A surge amount applies to this service time.',
            data: Object.assign({}, state, { schedule, surge_amount: data.surge_amount })
          }, this.getServiceScheduleContextPatch(state, 'service_scheduling'));
          return;
        }
        this.finalizeServiceSchedule(schedule, state);
      },
      () => {
        this.clearTyping();
        this.addAiText('I could not check that service slot right now.');
      }
    );
  }

  confirmServiceSurge(actionData?: any) {
    const state = this.restoreServiceActionState(actionData);
    const schedule = Object.assign({}, actionData && actionData.schedule || this.buildSelectedServiceSchedule(state), {
      surge_amount: actionData && actionData.surge_amount
    });
    this.finalizeServiceSchedule(schedule, state);
  }

  cancelServiceSurge() {
    this.removeChoiceMessages('service-surge-confirm');
    this.pendingServiceSchedule = null;
    this.selectedServiceSchedule = null;
    this.addAssistantMessage({
      role: 'ai',
      type: 'service-schedule-cancelled',
      text: 'No service time was added. Choose another slot when you are ready.'
    }, {
      pendingAction: '',
      pendingServiceSchedule: null,
      selectedServiceSchedule: null,
      pendingStartTime: '',
      pendingEndTime: '',
      pendingUnitCount: ''
    });
  }

  continueAfterServiceSchedule(actionData?: any) {
    this.restoreProductActionState(actionData);
    const schedule = actionData && (actionData.schedule || actionData.selectedServiceSchedule);
    if (schedule) {
      this.selectedServiceSchedule = schedule;
    }
    this.prepareProductOptions();
  }

  private finalizeServiceSchedule(schedule: any, state: any) {
    this.selectedServiceSchedule = schedule;
    this.pendingServiceSchedule = Object.assign({}, state, { selectedServiceSchedule: schedule });
    if (this.selectedProduct) {
      this.selectedProduct.start_time = schedule.start_time;
      this.selectedProduct.end_time = schedule.end_time;
      this.selectedProduct.unit_count = schedule.unit_count;
      if (schedule.agent_id) {
        this.selectedProduct.agent_id = schedule.agent_id;
      }
      if (schedule.agent_tag) {
        this.selectedProduct.agent_tag = schedule.agent_tag;
        this.selectedProduct.tag = schedule.agent_tag;
      }
      if (schedule.surge_amount) {
        this.selectedProduct.surge_amount = schedule.surge_amount;
      }
    }
    this.addAssistantMessage({
      role: 'ai',
      type: 'service-schedule-summary',
      text: 'Service time selected',
      data: Object.assign({}, state, { schedule, selectedServiceSchedule: schedule })
    }, this.getServiceScheduleContextPatch(Object.assign({}, state, { selectedServiceSchedule: schedule }), 'service_schedule_selected'));
  }

  private shouldScheduleServiceProduct(product: any, merchant: any): boolean {
    const context = this.getStoreBusinessContext(merchant, product);
    if (context.businessType !== BusinessType.SERVICE_MARKETPLACE) {
      return false;
    }
    if (context.taskType === TaskType.SERVICE_AS_PRODUCT) {
      return false;
    }
    return true;
  }

  private getServiceSchedulingMode(product: any, merchant: any): string {
    if (!this.shouldScheduleServiceProduct(product, merchant)) {
      return 'none';
    }
    if (Number(product && product.is_agents_on_product_tags_enabled) === 1) {
      return 'agent_first';
    }
    if (Number(product && product.enable_tookan_agent) === 1) {
      return 'assigned_agent_slots';
    }
    return 'product_slots';
  }

  private getStoreBusinessContext(merchant: any, product?: any): any {
    const appData = this.getSessionObject('appData') || {};
    const info = this.getSessionObject('info') || {};
    const config = this.getSessionObject('config') || {};
    const formSettings = this.getFirstObject(appData.formSettings || appData.form_settings);
    const configFormSettings = this.getFirstObject(config.formSettings || config.form_settings);
    const contextStoreId = this.getMerchantId(merchant) || this.getProductStoreId(product);
    const sameInfoStore = !contextStoreId || String(this.getMerchantId(info)) === String(contextStoreId);
    const sources = [merchant || {}];
    if (sameInfoStore) {
      sources.push(info);
    }
    [this.selectedProductMerchant, this.selectedMerchant, this.lastMerchantContext].forEach(source => {
      if (this.isSameStoreContext(source, merchant) || this.isSameStoreId(source, contextStoreId)) {
        sources.push(source);
      }
    });
    sources.push(formSettings, configFormSettings, config, product || {});
    let businessType = this.firstNumericField(sources, ['business_type', 'businessType']);
    if (!businessType && this.hasServiceSchedulingSignal(product)) {
      businessType = BusinessType.SERVICE_MARKETPLACE;
    }
    return {
      businessType,
      taskType: this.firstNumericField(sources, ['pd_or_appointment', 'pdOrAppointment', 'task_type'])
    };
  }

  private getFirstObject(value: any): any {
    if (Array.isArray(value)) {
      return value.length ? value[0] || {} : {};
    }
    return value || {};
  }

  private isSameStoreContext(source: any, merchant: any): boolean {
    if (!source || !merchant) {
      return false;
    }
    if (source === merchant) {
      return true;
    }
    const sourceId = this.getMerchantId(source);
    const merchantId = this.getMerchantId(merchant);
    return !!(sourceId && merchantId && String(sourceId) === String(merchantId));
  }

  private isSameStoreId(source: any, storeId: any): boolean {
    const sourceId = this.getMerchantId(source);
    return !!(sourceId && storeId && String(sourceId) === String(storeId));
  }

  private getProductStoreId(product: any): any {
    return product && (
      product.storefront_user_id ||
      product.storefrontUserId ||
      product.user_id ||
      product.userId ||
      product.seller_id ||
      product.sellerId ||
      product.storeId ||
      product.merchant_id ||
      product.merchantId
    );
  }

  private hasServiceSchedulingSignal(product: any): boolean {
    return !!(product && (
      Number(product.is_agents_on_product_tags_enabled) === 1 ||
      Number(product.enable_tookan_agent) === 1
    ));
  }

  private firstNumericField(sources: any[], keys: string[]): number {
    for (let i = 0; i < sources.length; i++) {
      for (let j = 0; j < keys.length; j++) {
        const value = sources[i] && sources[i][keys[j]];
        if (value !== undefined && value !== null && value !== '') {
          const parsed = Number(value);
          if (!isNaN(parsed)) {
            return parsed;
          }
        }
      }
    }
    return 0;
  }

  private buildServiceScheduleState(extra?: any): any {
    return Object.assign({
      mode: '',
      product: this.selectedProduct,
      merchant: this.selectedProductMerchant || this.selectedMerchant,
      quantity: this.selectedQuantity,
      selectedAgent: null,
      selectedDate: this.formatServiceDate(new Date()),
      dateAvailability: {},
      slotStatusMessage: '',
      dateOptions: this.getServiceDateOptions(),
      slots: [],
      startSlots: [],
      endSlots: []
    }, extra || {});
  }

  private restoreServiceActionState(actionData?: any): any {
    this.restoreProductActionState(actionData);
    const state = this.buildServiceScheduleState(Object.assign({}, this.pendingServiceSchedule || {}, actionData || {}));
    this.pendingServiceSchedule = state;
    return state;
  }

  private restoreServiceScheduleState(actionData?: any) {
    if (!actionData) {
      return;
    }
    if (actionData.selectedServiceSchedule || actionData.schedule) {
      this.selectedServiceSchedule = actionData.selectedServiceSchedule || actionData.schedule;
    }
    if (actionData.pendingServiceSchedule) {
      this.pendingServiceSchedule = actionData.pendingServiceSchedule;
    } else if (actionData.mode || actionData.selectedDate || actionData.selectedAgent || actionData.slots) {
      this.pendingServiceSchedule = this.buildServiceScheduleState(actionData);
    }
    if (this.pendingServiceSchedule) {
      this.pendingServiceSchedule.dateOptions = this.applyServiceDateAvailability(this.pendingServiceSchedule);
    }
    if (Array.isArray(actionData.agents)) {
      this.serviceAgents = actionData.agents;
    }
    if (Array.isArray(actionData.startSlots)) {
      this.serviceStartSlots = actionData.startSlots;
    }
    if (Array.isArray(actionData.endSlots)) {
      this.serviceEndSlots = actionData.endSlots;
    }
  }

  private getServiceScheduleContextPatch(state: any, pendingAction: string): any {
    return {
      selectedMerchant: state.merchant,
      selectedProduct: state.product,
      selectedAgent: state.selectedAgent,
      selectedServiceSchedule: state.selectedServiceSchedule || this.selectedServiceSchedule,
      pendingServiceSchedule: state,
      pendingAction,
      pendingQuantity: this.selectedQuantity,
      serviceSchedulingMode: state.mode,
      pendingServiceDate: state.selectedDate,
      pendingStartTime: state.start_time,
      pendingEndTime: state.end_time,
      pendingUnitCount: state.unit_count
    };
  }

  private syncServiceScheduleContext(state: any, pendingAction: string) {
    this.syncContext(this.getServiceScheduleContextPatch(state, pendingAction));
  }

  private buildServiceAgentsPayload(state: any): any {
    const payload = this.buildServiceBasePayload(state);
    payload.product_id = state.product && (state.product.product_id || state.product.id);
    return payload;
  }

  private buildServiceSlotsPayload(state: any): any {
    const payload = this.buildServiceBasePayload(state);
    payload.product_id = state.product && (state.product.product_id || state.product.id);
    payload.date = state.selectedDate || this.formatServiceDate(new Date());
    payload.is_new_flow = 1;
    payload.is_manual_date_selection = true;
    payload.self_pickup = this.getSelfPickupForScheduling(state.merchant);
    if (state.selectedAgent && Number(state.product && state.product.is_agents_on_product_tags_enabled) === 1) {
      payload.agent_id = state.selectedAgent.fleet_id;
      if (state.selectedAgent.tag) {
        payload.agent_tag = state.selectedAgent.tag;
      }
    }
    return payload;
  }

  private buildServiceAvailabilityPayload(state: any): any {
    const payload = this.buildServiceBasePayload(state);
    payload.product_id = state.product && (state.product.product_id || state.product.id);
    payload.start_time = state.start_time;
    payload.end_time = state.end_time;
    payload.currency_id = this.getCurrencyId(state.product, state.merchant);
    payload.timezone = new Date().getTimezoneOffset();
    if (state.selectedAgent && Number(state.product && state.product.is_agents_on_product_tags_enabled) === 1) {
      payload.agent_id = state.selectedAgent.fleet_id;
    }
    return payload;
  }

  private buildServiceBasePayload(state: any): any {
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    const location = this.getSessionObject('location') || {};
    const merchant = state && state.merchant || {};
    const product = state && state.product || {};
    const payload: any = {
      marketplace_user_id: config.marketplace_user_id || vendor.marketplace_user_id,
      user_id: product.user_id || this.getMerchantId(merchant),
      language: this.getSessionString('language') || 'en',
      domain_name: config.domain_name,
      latitude: location.latitude || location.lat,
      longitude: location.longitude || location.lng
    };
    if (vendor.vendor_id) {
      payload.vendor_id = vendor.vendor_id;
      payload.access_token = vendor.app_access_token;
    }
    return payload;
  }

  private normalizeAgents(response: any): any[] {
    const data = this.extractApiData(response);
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.agents)) {
      return data.agents;
    }
    return [];
  }

  private normalizeServiceSlots(data: any, state: any): any[] {
    const rawSlots = Array.isArray(data) ? data : data && Array.isArray(data.slots) ? data.slots : [];
    const slots = [];
    rawSlots.forEach(slot => {
      if (!this.isServiceSlotAvailable(slot)) {
        return;
      }
      const start = slot.slot_start_time || slot.start_time || slot.date;
      let end = slot.slot_end_time || slot.end_time;
      if (!start) {
        return;
      }
      if (!end && this.hasFixedServiceDuration(state.product)) {
        end = this.addMinutesToIso(start, Number(state.product.service_time) || 0);
      }
      const startMoment = moment(start);
      if (!startMoment.isValid()) {
        return;
      }
      slots.push({
        start_time: start,
        end_time: end,
        label: end ? this.formatServiceSlotRange(start, end) : this.formatServiceSlotTime(start),
        timeLabel: this.formatServiceSlotTime(start),
        raw: slot
      });
    });
    return slots.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  }

  private isServiceSlotAvailable(slot: any): boolean {
    if (!slot) {
      return false;
    }
    if ('available_status' in slot && Number(slot.available_status) !== 0) {
      return false;
    }
    if ('is_booked' in slot && Number(slot.is_booked) !== 0) {
      return false;
    }
    return true;
  }

  private getServiceEndSlots(state: any, slots: any[]): any[] {
    const startTime = state && state.start_time;
    if (!startTime) {
      return [];
    }
    const startMs = new Date(startTime).getTime();
    return (slots || []).filter(slot => new Date(slot.start_time).getTime() > startMs).map(slot => Object.assign({}, slot, {
      endSelectionTime: slot.start_time,
      label: this.formatServiceSlotTime(slot.start_time)
    }));
  }

  private buildSelectedServiceSchedule(state: any, surgeAmount?: any): any {
    const agent = state.selectedAgent || {};
    const schedule: any = {
      start_time: state.start_time,
      end_time: state.end_time,
      unit_count: state.unit_count || 1,
      mode: state.mode,
      selectedDate: state.selectedDate,
      label: this.formatServiceSlotRange(state.start_time, state.end_time)
    };
    if (agent.fleet_id) {
      schedule.agent_id = agent.fleet_id;
      schedule.agent_name = this.getAgentName(agent);
      schedule.agent_tag = agent.tag;
    }
    if (surgeAmount) {
      schedule.surge_amount = surgeAmount;
    }
    return schedule;
  }

  private getServiceDateOptions(): any[] {
    const config = this.getSessionObject('config') || {};
    const limit = Math.min(Math.max(Number(config.max_schedule_days_limit) || 7, 1), 7);
    const dates = [];
    for (let i = 0; i < limit; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const value = this.formatServiceDate(date);
      dates.push({
        value,
        label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : moment(date).format('MMM D'),
        meta: moment(date).format('ddd')
      });
    }
    return dates;
  }

  getServiceDateAvailability(dateOption: any): string {
    const status = dateOption && dateOption.availability;
    if (status === true || status === 'available') {
      return 'available';
    }
    if (status === false || status === 'unavailable') {
      return 'unavailable';
    }
    return 'unknown';
  }

  getServiceDateAvailabilityLabel(dateOption: any): string {
    const status = this.getServiceDateAvailability(dateOption);
    if (status === 'available') {
      return 'Slots available';
    }
    if (status === 'unavailable') {
      return 'No slots';
    }
    return 'Check slots';
  }

  private markServiceDateAvailability(state: any, dateValue: string, hasSlots: boolean) {
    if (!state || !dateValue) {
      return;
    }
    const availability = Object.assign({}, state.dateAvailability || {});
    availability[dateValue] = hasSlots ? 'available' : 'unavailable';
    state.dateAvailability = availability;
    state.dateOptions = this.applyServiceDateAvailability(state);
  }

  private applyServiceDateAvailability(state: any): any[] {
    const dateOptions = state && state.dateOptions && state.dateOptions.length ? state.dateOptions : this.getServiceDateOptions();
    const availability = state && state.dateAvailability ? state.dateAvailability : {};
    const selectedDate = state && state.selectedDate;
    return dateOptions.map(option => {
      const value = option && option.value;
      const optionAvailability = value && availability[value] ? availability[value] : option.availability;
      return Object.assign({}, option, {
        availability: optionAvailability || 'unknown',
        selected: !!(selectedDate && value === selectedDate)
      });
    });
  }

  private refreshServiceDateOptionsMessage(state: any) {
    if (!state) {
      return;
    }
    state.dateOptions = this.applyServiceDateAvailability(state);
    for (let index = this.messages.length - 1; index >= 0; index--) {
      const message = this.messages[index];
      if (message && message.type === 'service-date-options') {
        this.messages[index] = Object.assign({}, message, {
          data: Object.assign({}, message.data || {}, state)
        });
        this.messages = this.messages.slice();
        return;
      }
    }
  }

  private getSelfPickupForScheduling(merchant: any): number {
    if (merchant && merchant.self_pickup) {
      const method = Number(this.getSessionString('deliveryMethod'));
      if ((method === 1 || method === 8) && !merchant.available && merchant.scheduled_task) {
        return 0;
      }
      return 1;
    }
    return this.getSelfPickupValue();
  }

  private getCurrencyId(product: any, merchant: any): number {
    const config = this.getSessionObject('config') || {};
    const sources = [product && product.payment_settings, merchant && merchant.payment_settings, config.payment_settings];
    for (let i = 0; i < sources.length; i++) {
      const source = Array.isArray(sources[i]) ? sources[i][0] : sources[i];
      if (source && source.currency_id) {
        return Number(source.currency_id);
      }
    }
    return 0;
  }

  private hasFixedServiceDuration(product: any): boolean {
    return Number(product && product.service_time) > 0;
  }

  private getServiceScheduleValidationError(state: any): string {
    if (!state.start_time) {
      return 'Please choose a start time.';
    }
    if (!state.end_time) {
      return 'Please choose an end time.';
    }
    const start = new Date(state.start_time).getTime();
    const end = new Date(state.end_time).getTime();
    const now = new Date().getTime();
    if (end <= start) {
      return 'End time should be greater than start time.';
    }
    if (start - now < 180000) {
      return 'Start time should be at least 3 minutes greater than current time.';
    }
    if ((end - start) / 60000 < 2) {
      return 'End time should be at least 2 minutes greater than start time.';
    }
    return '';
  }

  private getServiceUnitCount(product: any, startTime: string, endTime: string): any {
    const unitType = Number(product && product.unit_type);
    const unit = Number(product && product.unit) || 1;
    const diffMinutes = Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000);
    let unitCount = 1;
    let multiple = 0;
    let label = 'minute(s)';
    if (unitType === 2) {
      unitCount = diffMinutes / unit;
      multiple = diffMinutes % unit;
      label = 'minute(s)';
    } else if (unitType === 3) {
      unitCount = diffMinutes / (unit * 60);
      multiple = diffMinutes % (unit * 60);
      label = 'hour(s)';
    } else if (unitType === 4) {
      unitCount = diffMinutes / 1440;
      multiple = diffMinutes % 1440;
      label = 'day(s)';
    } else if (unitType === 5) {
      const diffDays = diffMinutes / 1440;
      unitCount = diffDays === 0 ? 1 : diffDays / 7;
      multiple = diffDays === 0 ? 0 : diffDays % 7;
      label = 'week(s)';
    } else if (unitType === 6) {
      const diffDays = diffMinutes / 1440;
      unitCount = diffDays === 0 ? 1 : diffDays / 30;
      multiple = diffDays === 0 ? 0 : diffDays % 30;
      label = 'month(s)';
    } else if (unitType === 7) {
      const diffDays = diffMinutes / 1440;
      unitCount = diffDays === 0 ? 1 : diffDays / 365;
      multiple = diffDays === 0 ? 0 : diffDays % 365;
      label = 'year(s)';
    }
    if (multiple || unitCount < 1) {
      return {
        error: 'Start time and end time should be a multiple of ' + unit + ' ' + label + '.'
      };
    }
    return {
      unitCount: Math.max(1, unitCount)
    };
  }

  private addMinutesToIso(value: string, minutes: number): string {
    return moment(value).add(minutes, 'minutes').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  }

  private formatServiceDate(date: Date): string {
    return moment(date).format('YYYY-MM-DD');
  }

  getServiceDateLabel(value: string): string {
    return value ? moment(value).format('MMM D, YYYY') : '';
  }

  getServiceSlotLabel(slot: any): string {
    return slot && slot.label || '';
  }

  getServiceScheduleSummary(schedule: any): string {
    if (!schedule) {
      return '';
    }
    const parts = [this.formatServiceSlotRange(schedule.start_time, schedule.end_time)];
    if (schedule.agent_name) {
      parts.push(schedule.agent_name);
    }
    return parts.filter(Boolean).join(' • ');
  }

  isBookingMessage(message: OrderAiMessage): boolean {
    return !!(message && [
      'service-agent-candidates',
      'service-date-options',
      'service-start-slots',
      'service-end-slots',
      'service-surge-confirm',
      'service-schedule-summary'
    ].indexOf(message.type) > -1);
  }

  private getLatestActionMessageIndex(): number {
    for (let index = (this.messages || []).length - 1; index >= 0; index--) {
      if (this.isActionMessage(this.messages[index])) {
        return index;
      }
    }
    return -1;
  }

  private getActionMessageTypes(): string[] {
    return this.actionMessageTypes;
  }

  getServiceBookingIntro(data: any): string {
    const productName = this.getProductName(data && data.product);
    const merchantName = this.getMerchantName(data && data.merchant);
    if (data && data.selectedAgent) {
      return 'Finish booking ' + productName + ' with ' + this.getAgentName(data.selectedAgent) + ' at ' + merchantName + '.';
    }
    return 'Choose the best available option for ' + productName + ' at ' + merchantName + '.';
  }

  getServiceDurationLabel(product: any, schedule?: any): string {
    if (product && Number(product.service_time) > 0) {
      return Number(product.service_time) + ' min';
    }
    if (schedule && schedule.start_time && schedule.end_time) {
      const diffMinutes = Math.round((new Date(schedule.end_time).getTime() - new Date(schedule.start_time).getTime()) / 60000);
      if (diffMinutes > 0) {
        return diffMinutes >= 60 && diffMinutes % 60 === 0 ? (diffMinutes / 60) + ' hr' : diffMinutes + ' min';
      }
    }
    const unit = Number(product && product.unit) || 1;
    const unitType = Number(product && product.unit_type);
    const label = this.getServiceUnitTypeLabel(unitType);
    return label ? unit + ' ' + label : 'Choose time';
  }

  getServiceSelectedAgentName(data: any): string {
    if (data && data.selectedAgent) {
      return this.getAgentName(data.selectedAgent);
    }
    if (data && data.schedule && data.schedule.agent_name) {
      return data.schedule.agent_name;
    }
    if (data && data.selectedServiceSchedule && data.selectedServiceSchedule.agent_name) {
      return data.selectedServiceSchedule.agent_name;
    }
    return 'Any available agent';
  }

  getServiceBookingPrice(data: any): string {
    const product = data && data.product;
    const merchant = data && data.merchant;
    const price = this.getProductPrice(product);
    if (!price) {
      return 'Price on confirmation';
    }
    return this.getDisplayCurrency(product, merchant) + price;
  }

  getAgentName(agent: any): string {
    return agent && (agent.name || agent.username || agent.login_id || agent.email) || 'Agent';
  }

  getAgentImage(agent: any): string {
    return agent && (agent.fleet_thumb_image || agent.fleet_image);
  }

  getAgentMeta(agent: any): string {
    const parts = [];
    if (agent && agent.tag) {
      parts.push(agent.tag);
    }
    if (agent && agent.distance) {
      parts.push(Number(agent.distance).toFixed(1) + ' km');
    }
    if (agent && agent.ratings) {
      parts.push('★ ' + Number(agent.ratings).toFixed(1));
    }
    return parts.join(' • ');
  }

  getAgentInitials(agent: any): string {
    const name = this.getAgentName(agent);
    const parts = name.split(' ').filter(Boolean);
    if (!parts.length) {
      return 'AI';
    }
    return parts.slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('');
  }

  getAgentRecommendationLabel(agent: any, index: number): string {
    if (index === 0) {
      return 'Recommended for you';
    }
    if (agent && agent.tag) {
      return agent.tag;
    }
    return 'Available specialist';
  }

  getAgentServiceLine(agent: any, data: any): string {
    if (agent && (agent.designation || agent.role || agent.category_name)) {
      return agent.designation || agent.role || agent.category_name;
    }
    if (agent && agent.tag) {
      return agent.tag + ' specialist';
    }
    return this.getProductName(data && data.product) + ' specialist';
  }

  getAgentRating(agent: any): string {
    const value = Number(agent && (agent.ratings || agent.rating || agent.fleet_rating || agent.avg_rating)) || 4.8;
    return value.toFixed(1);
  }

  getAgentCompletedText(agent: any): string {
    const completed = Number(agent && (agent.completed_jobs || agent.completedJobs || agent.orders_completed || agent.jobs_completed));
    if (completed) {
      return completed + '+ services completed';
    }
    return '100+ services completed';
  }

  getAgentDescription(agent: any, data: any): string {
    const description = agent && (agent.description || agent.bio || agent.profile_description || agent.about);
    if (description) {
      return description;
    }
    return 'Experienced provider for ' + this.getProductName(data && data.product) + ', with a focus on reliable service and smooth customer experience.';
  }

  getAgentTags(agent: any, data: any): string[] {
    const tags = [];
    if (agent && agent.tag) {
      tags.push(agent.tag);
    }
    if (agent && agent.distance) {
      tags.push(Number(agent.distance).toFixed(1) + ' km away');
    }
    tags.push(this.getProductName(data && data.product));
    tags.push('Top rated');
    const unique = [];
    tags.forEach(tag => {
      if (tag && unique.indexOf(tag) === -1 && unique.length < 4) {
        unique.push(tag);
      }
    });
    return unique;
  }

  getServiceDateWeekday(value: string): string {
    return value ? moment(value).format('ddd').toUpperCase() : '';
  }

  getServiceDateDay(value: string): string {
    return value ? moment(value).format('D') : '';
  }

  getServiceDateMonth(value: string): string {
    return value ? moment(value).format('MMM YYYY') : '';
  }

  getServiceSlotTimeOnly(slot: any): string {
    return slot && slot.start_time ? this.formatServiceSlotTime(slot.start_time) : this.getServiceSlotLabel(slot);
  }

  private getServiceUnitTypeLabel(unitType: number): string {
    if (unitType === 2) {
      return 'min';
    }
    if (unitType === 3) {
      return 'hr';
    }
    if (unitType === 4) {
      return 'day';
    }
    if (unitType === 5) {
      return 'week';
    }
    if (unitType === 6) {
      return 'month';
    }
    if (unitType === 7) {
      return 'year';
    }
    return '';
  }

  private formatServiceSlotRange(start: string, end: string): string {
    if (!start && !end) {
      return '';
    }
    if (!end) {
      return this.formatServiceSlotTime(start);
    }
    return this.formatServiceSlotTime(start) + ' - ' + this.formatServiceSlotTime(end);
  }

  private formatServiceSlotTime(value: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (!date.getTime()) {
      return value;
    }
    const adjusted = new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
    const config = this.getSessionObject('config') || {};
    return moment(adjusted).format(Number(config.time_format) === 2 ? 'HH:mm' : 'hh:mm A');
  }

  private buildParsePayload(prompt: string) {
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    const location = this.getSessionObject('location') || {};
    return {
      prompt,
      sessionId: this.cleanSessionId(this.sessionId),
      marketplaceUserId: config.marketplace_user_id,
      marketplace_user_id: config.marketplace_user_id,
      userId: this.sessionService.getString('user_id') || config.marketplace_user_id,
      user_id: config.marketplace_user_id,
      language: this.getSessionString('language') || 'en',
      domainName: config.domain_name,
      domain_name: config.domain_name,
      latitude: location.latitude || location.lat,
      longitude: location.longitude || location.lng,
      deliveryMethod: this.getSessionObject('self_pickup') || 0,
      appType: 'WEB',
      app_type: 'WEB',
      vendor_id: vendor.vendor_id,
      access_token: vendor.app_access_token
    };
  }

  private buildMarketplacePayload(searchText: string, includeDate: boolean) {
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    const location = this.getSessionObject('location') || {};
    const payload: any = {
      marketplace_user_id: config.marketplace_user_id,
      search_text: searchText,
      user_id: config.marketplace_user_id,
      latitude: location.latitude || location.lat,
      longitude: location.longitude || location.lng,
      app_type: 'WEB',
      vendor_id: vendor.vendor_id,
      access_token: vendor.app_access_token,
      self_pickup: this.getSelfPickupValue(),
      domain_name: config.domain_name,
      dual_user_key: 0,
      language: this.getSessionString('language') || 'en'
    };
    if (includeDate) {
      payload.date_time = new Date().toISOString();
    }
    return payload;
  }

  private buildMerchantProductSearchPayload(searchText: string, merchant: any) {
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    const payload: any = {
      marketplace_user_id: config.marketplace_user_id,
      search_text: searchText,
      user_id: String(this.getMerchantId(merchant)),
      date_time: new Date().toISOString(),
      domain_name: config.domain_name,
      dual_user_key: 0,
      language: this.getSessionString('language') || 'en'
    };
    if (vendor.vendor_id) {
      payload.vendor_id = vendor.vendor_id;
      payload.access_token = vendor.app_access_token;
    }
    return payload;
  }

  private buildDeliverectPayload() {
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    return {
      marketplace_user_id: config.marketplace_user_id,
      user_id: this.sessionService.getString('user_id') || config.marketplace_user_id,
      vendor_id: vendor.vendor_id,
      access_token: vendor.app_access_token
    };
  }

  private normalizeMerchants(response: any): any[] {
    const data = response && response.data ? response.data : response;
    if (!data) {
      return [];
    }
    if (Array.isArray(data.result)) {
      return data.result;
    }
    if (Array.isArray(data.merchants)) {
      return data.merchants;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }

  private isApiError(response: any): boolean {
    return !!(response && response.status && response.status !== 200);
  }

  private getSelfPickupValue(): number {
    return this.getSessionString('deliveryMethod') === '2' ? 1 : 0;
  }

  private normalizeProducts(response: any): any[] {
    const data = response && response.data ? response.data : response;
    const result = data && Array.isArray(data.result) ? data.result : Array.isArray(data) ? data : [];
    const selectedMerchantId = this.getMerchantId(this.selectedMerchant);
    const products = [];
    result.forEach(group => {
      const merchant = group.storefront_user_id || group.user_id || group.name ? group : this.selectedMerchant;
      if (selectedMerchantId && merchant && this.getMerchantId(merchant) && this.getMerchantId(merchant) !== selectedMerchantId) {
        return;
      }
      const list = group.product_list || group.products || group.items || [];
      if (Array.isArray(list)) {
        list.forEach(product => products.push({ product, merchant: merchant || this.selectedMerchant }));
      } else if (group.product_id || group.id) {
        products.push({ product: group, merchant: this.selectedMerchant });
      }
    });
    return products;
  }

  private normalizeMerchantProducts(response: any, merchant: any): any[] {
    const data = response && response.data ? response.data : response;
    const products = [];
    let list = [];
    if (Array.isArray(data)) {
      list = data;
    } else if (data && Array.isArray(data.result)) {
      list = data.result;
    } else if (data && Array.isArray(data.products)) {
      list = data.products;
    } else if (data && Array.isArray(data.product_list)) {
      list = data.product_list;
    }
    list.forEach(item => {
      const itemMerchant = item && (item.storefront_user_id || item.user_id || item.store_name) ? item : merchant;
      const productList = item && (item.product_list || item.products || item.items);
      if (Array.isArray(productList)) {
        productList.forEach(product => products.push({ product, merchant: itemMerchant || merchant }));
        return;
      }
      if (item) {
        products.push({ product: item, merchant });
      }
    });
    return products;
  }

  private normalizeCustomizations(product: any): any[] {
    if (!product) {
      return [];
    }
    const customizations = product.customization || product.customizations || product.original_customization || [];
    return Array.isArray(customizations) ? JSON.parse(JSON.stringify(customizations)) : [];
  }

  private normalizeTemplateFields(template: any): any[] {
    if (!template) {
      return [];
    }
    const fields = Array.isArray(template) ? template : template.templates || template.template || [];
    return Array.isArray(fields) ? fields.map(field => Object.assign({}, field, {
      value: field.value || (this.isTemplateMultiSelect(field) ? [] : '')
    })) : [];
  }

  private getCartShowPrice(product: any, merchant: any, serviceSchedule: any, unitPrice: number, templatePrice: number, addonPrice: number): number {
    if (!serviceSchedule || !this.shouldScheduleServiceProduct(product, merchant)) {
      return unitPrice + templatePrice + addonPrice;
    }
    return this.getServiceBasePrice(product, serviceSchedule, unitPrice) +
      templatePrice +
      addonPrice +
      (Number(serviceSchedule.surge_amount) || 0);
  }

  private getServiceBasePrice(product: any, serviceSchedule: any, fallbackPrice: number): number {
    const check = this.cartService.convertToSec(Object.assign({}, product, {
      unit_type: Number(product && product.unit_type)
    }));
    if (check && check.type === 1) {
      return Number(check.value) || fallbackPrice;
    }
    const converterToSec = Number(check && check.value) || 0;
    const unit = Number(product && product.unit) || 1;
    if (!converterToSec || !unit) {
      return fallbackPrice;
    }
    const pricePerUnitTime = (Number(product && product.price) || fallbackPrice) / unit;
    let timeDiff = 0;
    if (serviceSchedule && serviceSchedule.start_time && serviceSchedule.end_time) {
      const startDateTime = new Date(serviceSchedule.start_time);
      const endDateTime = new Date(serviceSchedule.end_time);
      timeDiff = (endDateTime.getTime() - startDateTime.getTime()) / 1000;
      timeDiff = timeDiff / converterToSec;
    }
    let serviceTime = product && product.service_time ? Number(product.service_time) / (converterToSec / 60) : timeDiff;
    if (!serviceTime) {
      serviceTime = 1;
    }
    return pricePerUnitTime * serviceTime;
  }

  private buildCartItem() {
    const product = this.selectedProduct || {};
    const merchant = this.selectedProductMerchant || this.selectedMerchant || {};
    const productId = product.product_id || product.id;
    const sellerId = product.seller_id || product.user_id || this.getMerchantId(merchant);
    const serviceSchedule = this.selectedServiceSchedule;
    const templateData = this.buildProductTemplate();
    const templatePrice = this.getTemplatePrice(templateData);
    const selectedCustomizations = this.buildSelectedCustomizations();
    const addonPrice = selectedCustomizations.reduce((sum, option) => sum + (Number(option.unit_price || option.price) || 0), 0);
    const unitPrice = this.getCartUnitPrice(product);
    const showPrice = this.getCartShowPrice(product, merchant, serviceSchedule, unitPrice, templatePrice, addonPrice);
    const cartItem: any = {
      id: productId,
      productId,
      product_id: productId,
      name: this.getProductName(product),
      quantity: this.selectedQuantity,
      minimum_quantity: this.getMinimumQuantity(product),
      maximum_quantity: Number(product.maximum_quantity) || 0,
      price: unitPrice,
      showPrice,
      totalPrice: showPrice * this.selectedQuantity,
      final_price: showPrice,
      productTemplatePrice: templatePrice,
      product_template: templateData.length ? JSON.stringify(templateData) : product.product_template,
      available_quantity: product.available_quantity,
      available_seller_quantity: product.available_seller_quantity,
      inventory_enabled: product.inventory_enabled,
      type: product.type || (product.layout_data && product.layout_data.buttons && product.layout_data.buttons[0] ? product.layout_data.buttons[0].type : 0),
      unit: product.unit,
      unit_type: product.unit_type,
      unit_count: product.unit_count,
      special_price: product.special_price,
      special_quantity: product.special_quantity,
      is_bundle: product.is_bundle,
      is_bolt: product.is_bolt,
      price_for_subscription: product.price_for_subscription,
      return_enabled: product.return_enabled,
      is_static_address_enabled: product.is_static_address_enabled,
      is_veg: product.is_veg,
      image_url: product.image_url,
      storeId: this.getMerchantId(merchant),
      user_id: product.user_id || this.getMerchantId(merchant),
      category_id: product.category_id || product.parent_category_id,
      is_product_template_enabled: product.is_product_template_enabled,
      original_customization: this.normalizeCustomizations(product),
      customizations: selectedCustomizations,
      layout_data: product.layout_data,
      payment_settings: product.payment_settings,
      service_time: product.service_time,
      delivery_by_merchant: merchant.delivery_by_merchant,
      enable_tookan_agent: product.enable_tookan_agent,
      is_agents_on_product_tags_enabled: product.is_agents_on_product_tags_enabled,
      seller_id: sellerId,
      agent_id: product.agent_id,
      agent_tag: product.agent_tag,
      tag: product.tag,
      is_recurring_enabled: product.is_recurring_enabled,
      often_bought_products: product.often_bought_products,
      surge_amount: product.surge_amount
    };
    if (serviceSchedule) {
      cartItem.start_time = serviceSchedule.start_time || serviceSchedule.start;
      cartItem.end_time = serviceSchedule.end_time || serviceSchedule.end;
      cartItem.unit_count = serviceSchedule.unit_count || cartItem.unit_count || 1;
      if (serviceSchedule.agent_id && product.is_agents_on_product_tags_enabled) {
        cartItem.agent_id = serviceSchedule.agent_id;
      }
      if (serviceSchedule.agent_tag && product.is_agents_on_product_tags_enabled) {
        cartItem.agent_tag = serviceSchedule.agent_tag;
        cartItem.tag = serviceSchedule.agent_tag;
      }
      if (serviceSchedule.surge_amount) {
        const surgeAmount = Number(serviceSchedule.surge_amount) || 0;
        cartItem.surge_amount = surgeAmount;
      }
    }
    return cartItem;
  }

  private buildSelectedCustomizations(): any[] {
    const selected = [];
    this.collectSelectedCustomizations(this.customizationGroups, selected);
    return selected;
  }

  private collectSelectedCustomizations(groups: any[], target: any[], parentPath?: string) {
    (groups || []).forEach(group => {
      const key = this.getCustomizationKey(group);
      const groupSelected = this.selectedCustomizations[key];
      const selectedList = Array.isArray(groupSelected) ? groupSelected : groupSelected ? [groupSelected] : [];
      selectedList.forEach(option => {
        const unitPrice = this.getOptionPrice(option);
        const custId = option.cust_id || option.id;
        const parentHash = option.parent_hash || (parentPath ? parentPath + '-' + (group.customize_id || group.id) + '-' + custId : null);
        target.push({
          cust_id: option.cust_id || option.id,
          unit_price: unitPrice,
          quantity: 1,
          total_price: unitPrice,
          name: this.getOptionName(option),
          parent_hash: parentHash
        });
        this.collectSelectedCustomizations(this.getSubCustomizationGroups(option), target, (group.customize_id || group.id) + '-' + custId);
      });
    });
  }

  private buildProductTemplate(): any[] {
    return this.templateFields.map(field => {
      const json: any = {
        display_name: field.display_name,
        data_type: field.data_type,
        label: field.key || field.label,
        allowed_values: field.allowed_values,
        value: field.value
      };
      if (this.isTemplateSelect(field)) {
        json.option = field.option || field.allowed_values;
        if (Array.isArray(field.value)) {
          json.cost = field.value.reduce((sum, item) => sum + (Number(item.cost) || 0), 0);
          json.value = field.value.map(item => item.text || item.value || item.name);
        } else if (field.value) {
          json.cost = Number(field.value.cost) || 0;
          json.value = field.value.text || field.value.value || field.value.name || '';
        }
      }
      return json;
    });
  }

  private getTemplatePrice(templateData: any[]): number {
    return templateData.reduce((sum, field) => sum + (Number(field.cost) || 0), 0);
  }

  // Catalog page has two entirely different validation semantics depending on integration
  // (see app-product.component.ts checkForMinimumSelectionAddonDeliverect vs
  // checkForMinimumSelectionAddon):
  //  - Deliverect-integrated merchants: independent min/max RANGE (min <= count <= max).
  //  - Generic (default, majority) merchants: minimum_selection_required, when set, means
  //    selected count must EXACTLY equal minimum_selection -- maximum_selection is not an
  //    independent bound at all for these merchants.
  private getCustomizationValidationError(groups: any[] = this.customizationGroups): string {
    for (let i = 0; i < (groups || []).length; i++) {
      const group = groups[i];
      const selectedOptions = this.getSelectedOptionsForGroup(group);
      const min = this.getCustomizationMinimum(group);
      const max = this.getCustomizationMaximum(group);
      const name = this.getCustomizationName(group);
      if (this.isDeliverectCustomizationMode(group)) {
        if (min > 0 && selectedOptions.length < min) {
          return 'Please select at least ' + min + ' option(s) for ' + name + '.';
        }
        if (max > 0 && selectedOptions.length > max) {
          return 'Please select up to ' + max + ' option(s) for ' + name + '.';
        }
      } else {
        if (min > 0 && selectedOptions.length !== min) {
          return 'Please select exactly ' + min + ' option(s) for ' + name + '.';
        }
        // Legacy layout: single-select group with no explicit minimum_selection_required
        // but a marked default option still requires exactly `minimum_selection` picked.
        if (!group.minimum_selection_required && this.getAddonLayoutType() === 1 &&
          !this.isMultiSelectCustomization(group) &&
          this.getCustomizationOptions(group).some(option => !!option.is_default)) {
          const legacyCount = Number(group.minimum_selection) || 1;
          if (selectedOptions.length !== legacyCount) {
            return 'Please select any ' + legacyCount + ' option(s) for ' + name + '.';
          }
        }
      }
      for (let j = 0; j < selectedOptions.length; j++) {
        const childError = this.getCustomizationValidationError(this.getSubCustomizationGroups(selectedOptions[j]));
        if (childError) {
          return childError;
        }
      }
    }
    return '';
  }

  private getMissingTemplateField() {
    return this.templateFields.find(field => {
      if (!field.is_required && !field.required) {
        return false;
      }
      return !field.value || (Array.isArray(field.value) && !field.value.length);
    });
  }

  private hasDifferentMerchantCart(merchant: any): boolean {
    const cart = this.sessionService.getByKey('app', 'cart');
    if (!cart || !cart.length) {
      return false;
    }
    const currentMerchantId = cart[0].storeId || cart[0].user_id;
    return currentMerchantId && this.getMerchantId(merchant) && String(currentMerchantId) !== String(this.getMerchantId(merchant));
  }

  private setMerchantSession(merchant: any, product?: any): any {
    if (!merchant) {
      return null;
    }
    const normalizedMerchant = this.buildMerchantSessionContext(merchant, product);
    const merchantId = normalizedMerchant.storefront_user_id || this.getMerchantId(normalizedMerchant);
    this.sessionService.set('info', normalizedMerchant);
    this.sessionService.set('stores', [normalizedMerchant]);
    this.sessionService.set('user_id', merchantId);
    return normalizedMerchant;
  }

  private buildMerchantSessionContext(merchant: any, product?: any): any {
    if (!merchant && !product) {
      return null;
    }
    const appData = this.getSessionObject('appData') || {};
    const info = this.getSessionObject('info') || {};
    const config = this.getSessionObject('config') || {};
    const formSettings = this.getFirstObject(appData.formSettings || appData.form_settings);
    const configFormSettings = this.getFirstObject(config.formSettings || config.form_settings);
    const selectedProduct = product || this.selectedProduct;
    const merchantId = this.getMerchantId(merchant) || this.getProductStoreId(selectedProduct);
    const refreshedMerchant = merchantId ? this.refreshedMerchantContexts[String(merchantId)] : null;
    const sameInfoStore = !!(merchantId && info && this.getMerchantId(info) &&
      String(this.getMerchantId(info)) === String(merchantId));
    const matchedContexts = [this.selectedProductMerchant, this.selectedMerchant, this.lastMerchantContext]
      .filter(source => this.isSameStoreContext(source, merchant) || this.isSameStoreId(source, merchantId));
    const normalized = Object.assign({}, merchant || {}, sameInfoStore ? info : {}, ...matchedContexts, refreshedMerchant || {});
    const storefrontId = normalized.storefront_user_id || merchantId || normalized.user_id || normalized.merchant_id || normalized.id;

    if (storefrontId) {
      normalized.storefront_user_id = storefrontId;
      normalized.user_id = normalized.user_id || storefrontId;
    }

    const fallbackSources = [
      sameInfoStore ? info : {},
      ...matchedContexts,
      formSettings,
      configFormSettings,
      config
    ];
    this.copyMissingFields(normalized, fallbackSources, [
      'business_type',
      'pd_or_appointment',
      'delivery_by_merchant',
      'scheduled_task',
      'instant_task',
      'available',
      'is_menu_enabled',
      'self_pickup',
      'home_delivery',
      'pick_and_drop',
      'enable_start_time_end_time',
      'enable_tookan_agent',
      'is_order_agent_scheduling_enabled',
      'is_static_address_enabled',
      'is_recurring_enabled',
      'multiple_product_single_cart',
      'merchantMinimumOrder',
      'minimum_self_pickup_amount',
      'merchant_delivery_time'
    ]);

    const businessContext = this.getStoreBusinessContext(normalized, selectedProduct);
    if (this.isMissingValue(normalized.business_type) && businessContext.businessType) {
      normalized.business_type = businessContext.businessType;
    }
    if (this.isMissingValue(normalized.pd_or_appointment) && businessContext.taskType) {
      normalized.pd_or_appointment = businessContext.taskType;
    }
    this.normalizeNumericFields(normalized, [
      'business_type',
      'pd_or_appointment',
      'delivery_by_merchant',
      'scheduled_task',
      'instant_task',
      'available',
      'is_menu_enabled',
      'self_pickup',
      'home_delivery',
      'pick_and_drop',
      'enable_start_time_end_time',
      'enable_tookan_agent',
      'is_order_agent_scheduling_enabled',
      'is_static_address_enabled',
      'is_recurring_enabled',
      'multiple_product_single_cart',
      'merchantMinimumOrder',
      'minimum_self_pickup_amount',
      'merchant_delivery_time'
    ]);

    return normalized;
  }

  private normalizeNumericFields(target: any, keys: string[]) {
    keys.forEach(key => {
      const value = target[key];
      if (this.isMissingValue(value)) {
        return;
      }
      const parsed = Number(value);
      if (!isNaN(parsed)) {
        target[key] = parsed;
      }
    });
  }

  private copyMissingFields(target: any, sources: any[], keys: string[]) {
    keys.forEach(key => {
      if (!this.isMissingValue(target[key])) {
        return;
      }
      const value = this.getFirstPresentValue(sources, key);
      if (!this.isMissingValue(value)) {
        target[key] = value;
      }
    });
  }

  private getFirstPresentValue(sources: any[], key: string): any {
    for (let i = 0; i < sources.length; i++) {
      const value = sources[i] && sources[i][key];
      if (!this.isMissingValue(value)) {
        return value;
      }
    }
    return undefined;
  }

  private isMissingValue(value: any): boolean {
    return value === undefined || value === null || value === '';
  }

  private shouldFetchDeliverectCustomization(): boolean {
    const merchant = this.selectedProductMerchant || this.selectedMerchant || {};
    return !!(this.selectedProduct && (this.selectedProduct.deliverect_id || this.selectedProduct.plu) &&
      (merchant.merchant_deliverect_enabled || (merchant.additional_fields && merchant.additional_fields.merchant_deliverect_enabled)));
  }

  private shouldHideForRoute(url: string): boolean {
    const route = (url || '').toLowerCase();
    return route.indexOf('/checkout') > -1 ||
      route.indexOf('/custom-checkout') > -1 ||
      route.indexOf('/payment') > -1 ||
      route.indexOf('payment-success') > -1 ||
      route.indexOf('order-success') > -1 ||
      route.indexOf('order-placed') > -1;
  }

  private getCurrentStoreContext(): any {
    const storeId = this.getCurrentStoreIdFromRoute();
    if (!storeId) {
      return null;
    }
    const info = this.getSessionObject('info');
    if (info && String(this.getMerchantId(info)) === String(storeId)) {
      return info;
    }
    const stores = this.getSessionObject('stores');
    if (Array.isArray(stores)) {
      const matchedStore = stores.find(store => String(this.getMerchantId(store)) === String(storeId));
      if (matchedStore) {
        return matchedStore;
      }
    }
    const config = this.getSessionObject('config') || {};
    const marketplaceStores = config.enabled_marketplace_storefront;
    if (Array.isArray(marketplaceStores)) {
      const matchedStore = marketplaceStores.find(store => String(this.getMerchantId(store)) === String(storeId));
      if (matchedStore) {
        return matchedStore;
      }
    }
    return null;
  }

  private fetchCurrentStoreFromRoute(searchText: string) {
    const storeId = this.getCurrentStoreIdFromRoute();
    if (!storeId) {
      this.searchProducts(searchText);
      return;
    }
    this.setTyping('Checking current store');
    this.catalogueService.getSingleRestaturant(this.buildSingleStorePayload(storeId)).pipe(timeout(OrderWithAiComponent.API_TIMEOUT_MS)).subscribe(
      response => {
        this.clearTyping();
        if (this.isApiError(response)) {
          this.searchProducts(searchText);
          return;
        }
        const data = response && response.data;
        const merchant = Array.isArray(data) ? data[0] : data;
        if (!merchant) {
          this.searchProducts(searchText);
          return;
        }
        this.askToSearchCurrentStore(merchant, searchText);
      },
      () => {
        this.clearTyping();
        this.searchProducts(searchText);
      }
    );
  }

  private buildSingleStorePayload(storeId: string): any {
    const config = this.getSessionObject('config') || {};
    const appData = this.getSessionObject('appData');
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    const location = this.getSessionObject('location') || {};
    const payload: any = {
      marketplace_reference_id: config.marketplace_reference_id,
      marketplace_user_id: config.marketplace_user_id,
      latitude: location.latitude || location.lat || 0,
      longitude: location.longitude || location.lng || 0,
      user_id: storeId
    };
    if (vendor.vendor_id) {
      payload.vendor_id = vendor.vendor_id;
      payload.access_token = vendor.app_access_token;
    }
    return payload;
  }

  private getCurrentStoreIdFromRoute(): string {
    const url = (this.router.url || '').split('?')[0].split('#')[0];
    const parts = url.split('/').filter(Boolean);
    const storeIndex = parts.indexOf('store');
    if (storeIndex === -1 || !parts[storeIndex + 2]) {
      return '';
    }
    return parts[storeIndex + 2];
  }

  private getSessionId(): string {
    let id = this.getSessionString('order_with_ai_session_id');
    const originalId = id;
    id = this.cleanSessionId(id);
    if (!id) {
      id = this.createSessionId();
      this.sessionService.setString('order_with_ai_session_id', id);
    } else if (id !== originalId) {
      this.sessionService.setString('order_with_ai_session_id', id);
    }
    return id;
  }

  private createSessionId(): string {
    return 'owai-' + Date.now() + '-' + Math.floor(Math.random() * 100000);
  }

  private getSessionObject(key: string): any {
    try {
      return this.sessionService.get(key);
    } catch (error) {
      return null;
    }
  }

  private getSessionString(key: string): string {
    try {
      return this.sessionService.getString(key);
    } catch (error) {
      return null;
    }
  }

  private cleanSessionId(id: string): string {
    if (!id) {
      return '';
    }
    try {
      id = decodeURIComponent(id);
    } catch (error) {}
    return String(id).replace(/^"|"$/g, '');
  }

  private getDefaultQuantity(product: any): number {
    const parsedQuantity = this.intent && this.intent.quantity ? Number(this.intent.quantity) : 1;
    return Math.max(this.getMinimumQuantity(product), parsedQuantity || 1);
  }

  private getMinimumQuantity(product: any): number {
    return Number(product && (product.minimum_quantity || product.min_quantity)) || 1;
  }

  getMerchantName(merchant: any): string {
    return merchant && (merchant.store_name || merchant.name || merchant.storefront_name || merchant.company_name || merchant.business_name) || 'Store';
  }

  getMerchantImage(merchant: any): string {
    return (merchant && (merchant.logo || merchant.thumb_url || merchant.banner_image)) || OrderWithAiComponent.IMAGE_PLACEHOLDER;
  }

  getMerchantMeta(merchant: any): string {
    const parts = [];
    if (merchant && merchant.display_address) {
      parts.push(merchant.display_address);
    } else if (merchant && merchant.address) {
      parts.push(merchant.address);
    }
    if (merchant && merchant.distance) {
      parts.push(merchant.distance);
    }
    return parts.join(' • ');
  }

  getMerchantTime(merchant: any): string {
    const minutes = merchant && (merchant.combined_time || merchant.estimatedTime || merchant.delivery_time);
    return minutes ? minutes + ' min' : '';
  }

  getProductName(product: any): string {
    return product && (product.name || product.product_name || product.item_name) || 'Product';
  }

  private getProductId(product: any): any {
    return product && (product.product_id || product.id);
  }

  getProductImage(product: any): string {
    return (product && (product.image_url || product.thumb_url || product.image || product.thumbnail)) || OrderWithAiComponent.IMAGE_PLACEHOLDER;
  }

  getMerchantId(merchant: any): any {
    return merchant && (
      merchant.storefront_user_id ||
      merchant.storefrontUserId ||
      merchant.user_id ||
      merchant.userId ||
      merchant.storeId ||
      merchant.merchant_id ||
      merchant.merchantId ||
      merchant.id
    );
  }

  getProductPrice(product: any): number {
    return Number(product && product.price) || 0;
  }

  getBrandLabel(): string {
    const config = this.sessionService.get('config') || {};
    const brandName = (config.form_name || '').toString().trim();
    return brandName ? `${brandName} AI` : 'AI';
  }

  // Single letter for the sidebar header's avatar circle -- the marketplace's own
  // initial (falls back to "A" for the generic "AI" label) rather than the full name,
  // same idea as any native chat app showing an avatar next to the conversation title.
  getBrandInitial(): string {
    const config = this.sessionService.get('config') || {};
    const brandName = (config.form_name || '').toString().trim();
    return (brandName ? brandName.charAt(0) : 'A').toUpperCase();
  }

  // The floating launcher is a small fixed-width pill -- a marketplace's full form_name
  // ("AllMart - Antigua's Local Marketplace AI") wraps and breaks the pill shape there.
  // Keep it generic on the launcher; the full brand name still shows once the panel is
  // open (sidebar title, header title -- see getBrandLabel()), where there's room for it.
  // order_with_ai_launcher_label -- translatable like the intro copy; English is only
  // the fallback until a marketplace's language strings define it.
  getLauncherLabel(): string {
    const strings = this.sessionService.languageStrings || {};
    return strings.order_with_ai_launcher_label || 'Order with AI';
  }

  // Every glow/highlight in this widget (launcher halo, intro card glow, active dot,
  // demo cart shadow) is bound to these two CSS custom properties -- set once on the
  // root .owai element (see the template) and consumed via var(--owai-accent, ...) /
  // rgba(var(--owai-accent-rgb, ...)) throughout the stylesheet -- rather than a
  // hardcoded color, so the whole thing tints to each marketplace's own theme color
  // instead of always being our green.
  getThemeAccentHex(): string {
    const config = this.sessionService.get('config') || {};
    const color = (config.color || '').toString().trim();
    return this.isHexColor(color) ? color : OrderWithAiComponent.DEFAULT_ACCENT_HEX;
  }

  getThemeAccentRgb(): string {
    return this.hexToRgbString(this.getThemeAccentHex()) || OrderWithAiComponent.DEFAULT_ACCENT_RGB;
  }

  private isHexColor(value: string): boolean {
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
  }

  private hexToRgbString(hex: string): string {
    let value = hex.replace('#', '');
    if (value.length === 3) {
      value = value.split('').map(c => c + c).join('');
    }
    const num = parseInt(value, 16);
    if (isNaN(num)) {
      return '';
    }
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `${r}, ${g}, ${b}`;
  }

  getDisplayCurrency(product?: any, merchant?: any): string {
    const config = this.sessionService.get('config') || {};
    if (config.is_multi_currency_enabled) {
      if (product && product.payment_settings && product.payment_settings.symbol) {
        return product.payment_settings.symbol;
      }
      if (merchant && merchant.payment_settings && merchant.payment_settings.symbol) {
        return merchant.payment_settings.symbol;
      }
    }
    return config.payment_settings && config.payment_settings[0] && config.payment_settings[0].symbol
      ? config.payment_settings[0].symbol
      : '';
  }

  getCustomizationName(group: any): string {
    return group && (group.name || group.title || group.display_name || group.label) || 'Option';
  }

  getCustomizationOptions(group: any): any[] {
    const options = group && (group.customize_options || group.options || group.option || group.addons || group.values);
    return Array.isArray(options) ? options : [];
  }

  getOptionName(option: any): string {
    return option && (option.name || option.title || option.display_name || option.text || option.value) || 'Option';
  }

  getOptionPrice(option: any): number {
    return Number(option && (option.price || option.cost || option.amount)) || 0;
  }

  getCustomizationHint(group: any): string {
    const min = this.getCustomizationMinimum(group);
    const max = this.getCustomizationMaximum(group);
    const deliverect = this.isDeliverectCustomizationMode(group);
    if (min > 0 && max > 0) {
      return min === max ? 'Choose ' + min : 'Choose ' + min + '-' + max;
    }
    if (min > 0) {
      if (min === 1) {
        return 'Required';
      }
      return deliverect ? 'Choose at least ' + min : 'Choose exactly ' + min;
    }
    if (max > 0) {
      return 'Choose up to ' + max;
    }
    return 'Optional';
  }

  getSelectedOptionsForGroup(group: any): any[] {
    const selected = this.selectedCustomizations[this.getCustomizationKey(group)];
    return Array.isArray(selected) ? selected : selected ? [selected] : [];
  }

  getSubCustomizationGroups(option: any): any[] {
    const groups = option && (option.sub_item || option.sub_items || option.children || option.customization);
    return Array.isArray(groups) ? groups : [];
  }

  getTemplateOptions(field: any): any[] {
    const options = field && (field.option || field.allowed_values || field.values);
    return Array.isArray(options) ? options : [];
  }

  isTemplateSelect(field: any): boolean {
    const type = String(field && field.data_type);
    return type === '5' || type === '6' || type.toLowerCase().indexOf('select') > -1;
  }

  isTemplateMultiSelect(field: any): boolean {
    const type = String(field && field.data_type);
    return type === '6' || type.toLowerCase().indexOf('multi') > -1;
  }

  private getCustomizationKey(group: any): string {
    return String(group && (group.customize_id || group.id || group._id || group.parent_hash || group.name || group.title || group.display_name));
  }

  private isRequiredCustomization(group: any): boolean {
    return this.getCustomizationMinimum(group) > 0 || !!(group && (group.is_required || group.required));
  }

  private isMultiSelectCustomization(group: any): boolean {
    if (group && (group.is_check_box === 1 || group.is_check_box === true)) {
      return true;
    }
    if (group && (group.is_check_box === 0 || group.is_check_box === false)) {
      return false;
    }
    return !!(group && (group.is_multiple || group.multiple || Number(group.max_selection) > 1 || Number(group.maximum) > 1 || Number(group.maximum_selection) > 1));
  }

  private getCustomizationMinimum(group: any): number {
    if (!group) {
      return 0;
    }
    if (group.minimum_selection_required === false || group.minimum_selection_required === 0) {
      return 0;
    }
    return Number(group.minimum_selection || group.min_selection || group.minimum || group.min || 0) || 0;
  }

  // Catalog page (app-product.component.ts) only enforces `maximum_selection` as an
  // independent cap for Deliverect-integrated merchants. Generic (non-Deliverect) merchants
  // never block additions on maximum_selection during selection, and never validate it at
  // submit time either -- `minimum_selection` alone doubles as an exact required count there
  // (see getCustomizationValidationError). Returning 0 here for generic merchants disables
  // both the toggle-time block and the submit-time range check, matching real behavior.
  private getCustomizationMaximum(group: any): number {
    if (!group || !this.isDeliverectCustomizationMode(group)) {
      return 0;
    }
    if (group.minimum_selection_required === false || group.minimum_selection_required === 0) {
      return 0;
    }
    return Number(group.maximum_selection || group.max_selection || group.maximum || group.max) || 0;
  }

  // Mirrors app-product.component.ts's gate: `appConfig.is_deliverect_enabled &&
  // restaurantInfo.additional_fields.merchant_deliverect_enabled`. Only Deliverect-integrated
  // merchants use independent min/max range validation; everyone else uses exact-match.
  private isDeliverectCustomizationMode(group?: any): boolean {
    const config = this.sessionService.get('config') || {};
    const merchant = this.selectedProductMerchant || this.selectedMerchant || {};
    const merchantFlag = !!(merchant.merchant_deliverect_enabled ||
      (merchant.additional_fields && merchant.additional_fields.merchant_deliverect_enabled));
    return !!(config.is_deliverect_enabled && merchantFlag);
  }

  private getAddonLayoutType(): number {
    const config = this.sessionService.get('config') || {};
    return Number(config.addon_layout_type) || 0;
  }

  private isSingleSelectMandatory(group: any): boolean {
    if (group && (group.is_singleSelect_mandatory === true || group.is_singleSelect_mandatory === 1)) {
      return true;
    }
    return !this.isMultiSelectCustomization(group) && (this.isRequiredCustomization(group) || this.getCustomizationOptions(group).some(option => !!option.is_default));
  }

  private applyDefaultCustomizations(groups: any[]) {
    (groups || []).forEach(group => {
      const key = this.getCustomizationKey(group);
      const defaults = this.getCustomizationOptions(group).filter(option => !!option.is_default);
      if (defaults.length && !this.selectedCustomizations[key]) {
        this.selectedCustomizations[key] = this.isMultiSelectCustomization(group) ? defaults.slice(0, this.getCustomizationMaximum(group) || defaults.length) : defaults[0];
      }
      this.getSelectedOptionsForGroup(group).forEach(option => this.applyDefaultCustomizations(this.getSubCustomizationGroups(option)));
    });
  }

  private getProductAvailabilityMessage(product: any, merchant: any): string {
    if (!product) {
      return 'I could not read that product. Please choose another product.';
    }
    if (!merchant) {
      return this.getMerchantName(merchant) + ' is not accepting online orders right now.';
    }
    if (!this.isMerchantActiveForOrdering(merchant)) {
      return this.getMerchantName(merchant) + ' is not accepting online orders right now.';
    }
    if (product.menu_enabled_product === 0) {
      return this.getProductName(product) + ' is not available in the active menu right now.';
    }
    if (product.inventory_enabled && Number(product.available_quantity) <= 0) {
      return this.getProductName(product) + ' is out of stock.';
    }
    if (product.snooze_datetime) {
      return this.getProductName(product) + ' is currently unavailable. It will be available later.';
    }
    return '';
  }

  private canAddAnotherProductForMerchant(merchant: any): boolean {
    const cart = this.sessionService.getByKey('app', 'cart');
    return !(merchant && Number(merchant.multiple_product_single_cart) === 2 && cart && cart.length);
  }

  private getAllowedMaxQuantity(product: any): number {
    const productMax = Number(product && product.maximum_quantity) || 0;
    const stockMax = product && product.inventory_enabled ? Number(product.available_quantity) || 0 : 0;
    if (productMax > 0 && stockMax > 0) {
      return Math.min(productMax, stockMax);
    }
    return productMax || stockMax || 0;
  }

  private getQuantityValidationMessage(product: any, quantity: number): string {
    const productName = this.getProductName(product);
    const min = this.getMinimumQuantity(product);
    const max = Number(product && product.maximum_quantity) || 0;
    const existingQuantity = this.getExistingProductQuantity(product);
    const totalQuantity = existingQuantity + Number(quantity || 0);
    if (quantity < min) {
      return 'Quantity of ' + productName + ' is less than minimum quantity ' + min + '.';
    }
    if (quantity > 9999 || totalQuantity > 9999) {
      return 'Total quantity for this item cannot be more than 9999.';
    }
    if (max > 0 && totalQuantity > max) {
      return 'Total quantity for ' + productName + ' cannot be more than maximum quantity ' + max + '.';
    }
    if (product && product.inventory_enabled && totalQuantity > Number(product.available_quantity || 0)) {
      return 'The selected quantity exceeds quantity available in stock.';
    }
    return '';
  }

  private getCartUnitPrice(product: any): number {
    const appData = this.sessionService.get('appData');
    const formSettings = appData && appData.formSettings ? appData.formSettings : {};
    const vendor = appData && appData.vendor_details ? appData.vendor_details : {};
    if (appData && vendor && product && product.is_bolt && Number(product.price_for_subscription) >= 0 &&
      Number(vendor.is_customer_subscription_plan_expired) === 0 && Number(formSettings.business_type) === 1) {
      return Number(product.price_for_subscription) || 0;
    }
    return Number(product && product.price) || 0;
  }

  private getExistingProductQuantity(product: any): number {
    const cartProduct = this.sessionService.getByKey('app', 'cartProduct');
    const productId = product && (product.product_id || product.id);
    return cartProduct && cartProduct[productId] ? Number(cartProduct[productId].quantity) || 0 : 0;
  }

  private getCheckoutValidationMessage(): string {
    const app = this.sessionService.get('app');
    const cart = app && app.cart ? app.cart : [];
    const merchant = this.selectedProductMerchant || this.selectedMerchant || this.sessionService.get('info');
    const location = this.sessionService.get('location');
    if (!cart.length) {
      return 'Your cart is empty.';
    }
    if (!location || !location.lat || !location.lng) {
      return 'Please select a delivery location before checkout.';
    }
    const preOrderError = this.getPreOrderCheckoutValidationMessage(merchant);
    if (preOrderError) {
      return preOrderError;
    }
    for (let i = 0; i < cart.length; i++) {
      if (cart[i].quantity < cart[i].minimum_quantity) {
        return 'Quantity of ' + cart[i].name + ' is less than minimum quantity ' + cart[i].minimum_quantity + '.';
      }
      if (cart[i].maximum_quantity > 0 && cart[i].quantity > cart[i].maximum_quantity) {
        return 'Quantity of ' + cart[i].name + ' is greater than maximum quantity ' + cart[i].maximum_quantity + '.';
      }
    }
    const minimumOrder = this.getMinimumOrderAmount(merchant);
    const total = this.getCartTotal(cart);
    if (!this.shouldSkipMinimumOrder() && minimumOrder > 0 && total < minimumOrder) {
      return 'Minimum order amount should be ' + this.getCurrencySymbol() + minimumOrder + '.';
    }
    const mandatoryError = this.getMandatoryCategoryError(cart);
    if (mandatoryError) {
      return mandatoryError;
    }
    const appData = this.sessionService.get('appData');
    const config = this.sessionService.get('config') || {};
    if (appData && Number(this.sessionService.getString('reg_status')) === 1 &&
      !(this.sessionService.get('info') && this.sessionService.get('info').is_single_storefront) &&
      Number(config.is_customer_verification_required) === 1 &&
      appData.vendor_details && appData.vendor_details.is_vendor_verified !== 1) {
      return 'Please complete customer verification before checkout.';
    }
    return '';
  }

  private clearPreOrderTimeForProductContext(product: any, merchant: any) {
    if (!this.isPreOrderTimeRelevantForMerchant(merchant, product)) {
      this.sessionService.remove('preOrderTime');
      return;
    }
    const preOrderTime = this.sessionService.getString('preOrderTime');
    if (preOrderTime && (new Date(preOrderTime)).setSeconds(0, 0) < (new Date()).setSeconds(0, 0)) {
      this.sessionService.remove('preOrderTime');
    }
  }

  private getPreOrderCheckoutValidationMessage(merchant: any): string {
    const preOrderTime = this.sessionService.getString('preOrderTime');
    if (!preOrderTime) {
      return '';
    }
    if (!this.isPreOrderTimeRelevantForMerchant(merchant)) {
      this.sessionService.remove('preOrderTime');
      return '';
    }
    if ((new Date(preOrderTime)).setSeconds(0, 0) < (new Date()).setSeconds(0, 0)) {
      return 'Please select a future date and time for pre-ordering.';
    }
    return '';
  }

  private isPreOrderTimeRelevantForMerchant(merchant: any, product?: any): boolean {
    const appData = this.getSessionObject('appData') || {};
    const config = this.getSessionObject('config') || {};
    const formSettings = this.getFirstObject(appData.formSettings || appData.form_settings);
    const configFormSettings = this.getFirstObject(config.formSettings || config.form_settings);
    const storeContext = Object.assign({}, product || {}, merchant || {});
    return !!(Number(formSettings.is_menu_enabled || configFormSettings.is_menu_enabled) &&
      Number(storeContext.is_menu_enabled) &&
      Number(storeContext.scheduled_task));
  }

  private canCheckoutWithoutLogin(): boolean {
    const appData = this.sessionService.get('appData');
    if (!appData) {
      return false;
    }
    return Number(this.sessionService.getString('reg_status')) === 1 || !(appData.signup_template_data && appData.signup_template_data.length);
  }

  private getMinimumOrderAmount(merchant: any): number {
    return this.getSelfPickupValue() ? Number(merchant && merchant.minimum_self_pickup_amount) || 0 : Number(merchant && merchant.merchantMinimumOrder) || 0;
  }

  private getCartTotal(cart: any[]): number {
    return (cart || []).reduce((sum, item) => sum + (Number(item.totalPrice) || (Number(item.showPrice) || Number(item.price) || 0) * Number(item.quantity || 0)), 0);
  }

  private shouldSkipMinimumOrder(): boolean {
    const config = this.sessionService.get('config') || {};
    return !!(+config.geofence_charge_enabled && Number(config.serving_restriction) === 2 && +config.is_charges_enabled);
  }

  private getCurrencySymbol(): string {
    return this.getDisplayCurrency(this.selectedProduct, this.selectedProductMerchant || this.selectedMerchant);
  }

  private initializeWidgetPosition() {
    if (typeof window === 'undefined') {
      return;
    }
    const saved = this.getSavedWidgetPosition();
    if (saved && typeof saved.x === 'number' && typeof saved.y === 'number' && isFinite(saved.x) && isFinite(saved.y)) {
      this.widgetPosition = saved;
    } else {
      this.widgetPosition = {
        x: window.innerWidth - this.launcherSize.width - this.viewportMargin,
        y: window.innerHeight - this.launcherSize.height - this.getBottomSafeArea()
      };
    }
    this.clampWidgetPosition();
  }

  private onDragMove(event: MouseEvent | TouchEvent) {
    if (!this.isDragging) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    const point = this.getPointerPoint(event);
    const deltaX = point.x - this.dragStart.pointerX;
    const deltaY = point.y - this.dragStart.pointerY;
    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
      this.suppressNextClick = true;
    }
    this.widgetPosition = this.clampPosition(
      this.dragStart.widgetX + deltaX,
      this.dragStart.widgetY + deltaY,
      this.launcherSize.width,
      this.launcherSize.height
    );
    this.syncWidgetCoordinates();
  }

  private endDrag() {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    this.saveWidgetPosition();
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup', this.boundDragEnd);
    document.removeEventListener('touchmove', this.boundDragMove);
    document.removeEventListener('touchend', this.boundDragEnd);
    document.removeEventListener('touchcancel', this.boundDragEnd);
  }

  private attachOutsideCloseListeners() {
    document.addEventListener('mousedown', this.boundOutsidePointer);
    document.addEventListener('touchstart', this.boundOutsidePointer);
  }

  private detachOutsideCloseListeners() {
    document.removeEventListener('mousedown', this.boundOutsidePointer);
    document.removeEventListener('touchstart', this.boundOutsidePointer);
  }

  private closeOnOutsidePointer(event: MouseEvent | TouchEvent) {
    if (!this.isOpen || this.isDragging) {
      return;
    }
    const target = event.target as Node;
    if (target && this.elementRef.nativeElement && this.elementRef.nativeElement.contains(target)) {
      return;
    }
    this.isOpen = false;
    this.detachOutsideCloseListeners();
    this.clampWidgetPosition();
  }

  private clampWidgetPosition() {
    this.isMobilePanelOpen = this.isMobileViewport() && this.isOpen;
    if (typeof window === 'undefined' || this.isMobilePanelOpen) {
      return;
    }
    if (this.isOpen) {
      const size = this.getPanelBounds();
      const panelPosition = this.clampPosition(this.widgetPosition.x, this.widgetPosition.y, size.width, size.height);
      this.widgetLeft = panelPosition.x;
      this.widgetTop = panelPosition.y;
      return;
    }
    this.widgetPosition = this.clampPosition(this.widgetPosition.x, this.widgetPosition.y, this.launcherSize.width, this.launcherSize.height);
    this.syncWidgetCoordinates();
    this.saveWidgetPosition();
  }

  private syncWidgetCoordinates() {
    this.widgetLeft = this.widgetPosition.x;
    this.widgetTop = this.widgetPosition.y;
  }

  private clampPosition(x: number, y: number, width: number, height: number) {
    if (typeof window === 'undefined') {
      return { x, y };
    }
    const maxX = Math.max(this.viewportMargin, window.innerWidth - width - this.viewportMargin);
    const maxY = Math.max(this.viewportMargin, window.innerHeight - height - this.getBottomSafeArea());
    return {
      x: Math.min(Math.max(this.viewportMargin, x), maxX),
      y: Math.min(Math.max(this.viewportMargin, y), maxY)
    };
  }

  private getPanelBounds() {
    if (typeof window === 'undefined') {
      return this.panelSize;
    }
    return {
      width: Math.min(this.panelSize.width, window.innerWidth - (this.viewportMargin * 2)),
      height: Math.min(this.panelSize.height, window.innerHeight - this.viewportMargin - this.getBottomSafeArea())
    };
  }

  // Used by getBottomSafeArea() below, for the floating launcher button only -- the
  // full-screen mobile chat panel intentionally covers the whole viewport, including
  // whatever bottom nav bar the marketplace has (see .owai--open in the stylesheet).
  private static readonly BOTTOM_BAR_SELECTORS = ['app-native-bottom', '.bottom-navbar', '.bottom-nav', 'app-nativenavigationmenu'];

  private getBottomSafeArea(): number {
    if (this.isMobileViewport() && typeof document !== 'undefined') {
      for (const selector of OrderWithAiComponent.BOTTOM_BAR_SELECTORS) {
        const bar = document.querySelector(selector) as HTMLElement;
        const barHeight = bar ? bar.getBoundingClientRect().height : 0;
        if (barHeight > 0) {
          return barHeight + this.viewportMargin;
        }
      }
    }
    return this.isMobileViewport() ? 88 : this.bottomSafeArea;
  }

  private getPointerPoint(event: MouseEvent | TouchEvent) {
    const touchEvent = event as TouchEvent;
    const touch = touchEvent.touches && touchEvent.touches.length
      ? touchEvent.touches[0]
      : touchEvent.changedTouches && touchEvent.changedTouches.length
        ? touchEvent.changedTouches[0]
        : null;
    return {
      x: touch ? touch.clientX : (event as MouseEvent).clientX,
      y: touch ? touch.clientY : (event as MouseEvent).clientY
    };
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 640;
  }

  private getSavedWidgetPosition() {
    try {
      const value = this.sessionService.getString('order_with_ai_widget_position');
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  }

  private saveWidgetPosition() {
    try {
      this.sessionService.setString('order_with_ai_widget_position', JSON.stringify(this.widgetPosition));
    } catch (error) {}
  }

  private getMandatoryCategoryError(cart: any[]): string {
    const mandatoryCategoryList = this.sessionService.get('requiredCategories') || [];
    if (!mandatoryCategoryList.length) {
      return '';
    }
    const categoriesInCart = (cart || []).map(item => item.category_id);
    const missing = mandatoryCategoryList.filter(item => categoriesInCart.indexOf(item.catalogue_id) === -1);
    return missing.length ? 'Please add required catalogue items before checkout.' : '';
  }

  private setTyping(label: string) {
    this.typingLabel = label;
    this.isTyping = true;
    this.scrollMessagesToBottom();
  }

  private clearTyping() {
    this.isTyping = false;
  }

  private addAiText(text: string) {
    this.addAssistantMessage({ role: 'ai', text });
  }

  private addFollowup(text: string) {
    this.addAssistantMessage({ role: 'ai', type: 'followup', text }, {
      pendingAction: 'followup'
    });
  }

  private isMerchantActiveForOrdering(merchant: any): boolean {
    return !!merchant &&
      (Number(merchant.available) === 1 ||
        Number(merchant.scheduled_task) === 1);
  }

  private removeChoiceMessages(type: string) {
    this.messages = this.messages.filter(message => message.type !== type);
  }

  private resetSelectionState(clearIntent = true) {
    if (clearIntent) {
      this.intent = null;
    }
    this.selectedMerchant = null;
    this.selectedProduct = null;
    this.selectedProductMerchant = null;
    this.selectedQuantity = 1;
    this.merchantCandidates = [];
    this.productCandidates = [];
    this.customizationGroups = [];
    this.selectedCustomizations = {};
    this.addonErrors = [];
    this.templateFields = [];
    this.selectedServiceSchedule = null;
    this.pendingServiceSchedule = null;
    this.serviceAgents = [];
    this.serviceStartSlots = [];
    this.serviceEndSlots = [];
    this.pendingProductCandidate = null;
    this.pendingCurrentStoreSearch = null;
  }
}
